import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material/styles'
import Adjust from '@mui/icons-material/Adjust'
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline'
import CircleIcon from '@mui/icons-material/Circle'
import HomeIcon from '@mui/icons-material/Home'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded'
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { ROUTES } from '@/utils/constants'
import { useAuthStore } from '@/stores/authStore'
import { testsApi } from '@/api/tests'
import { getErrorMessage } from '@/utils/error'
import { getUserDisplayName } from '@/utils/userName'
import { formatDateTimeDMY } from '@/utils/date'
import { useTestRealtime } from '@/realtime/useTestRealtime'
import { CardClues } from '@/components/cards/CardClues'
import { TestScoreGauge } from '@/components/tests/TestScoreGauge'
import type { Card as CardModel } from '@/types/cards'
import type { Clue } from '@/types'
import type { Test, TestAnswer, TestItem, TestParticipant } from '@/types'
import {
  allowedAnswerTypesForSkill,
  answerTypeSupportsAutoChecking,
  answerTypeFormatMode,
  isSpeechAnswerType,
  PROMPT_AUDIO,
  PROMPT_SOURCE_TEXT,
  PROMPT_TRANSLATION_TEXT,
  itemAllowsStudentHintsByGoals,
  itemAllowsStudentTranscriptionByGoals,
  normalizeSkillType,
} from '@/utils/testPromptAnswerMatrix'
import type { TFunction } from 'i18next'
import { isTypingTarget } from '@/utils/hotkeys'

function testTargetUserId(run: Pick<Test, 'target_type' | 'target_id'>): number | null {
  if (run.target_type === 'user' && run.target_id != null) return run.target_id
  return null
}

function testTargetGroupId(run: Pick<Test, 'target_type' | 'target_id'>): number | null {
  if (run.target_type === 'group' && run.target_id != null) return run.target_id
  return null
}

function testIsGroup(run: Pick<Test, 'target_type'>): boolean {
  return run.target_type === 'group'
}

/**
 * Teacher UI — только если текущий пользователь ведёт этот тест (учитель урока).
 * Student UI — если он в run.participants (тест у своего учителя).
 * Глобальные slug admin/teacher/student на выбор интерфейса не влияют.
 */
function resolveIsTeacherViewInTest(run: Test | null, authUserId: number | null): boolean {
  if (!run || authUserId == null) return false

  const lessonTeacherId = run.lesson?.teacher_id
  if (lessonTeacherId != null && lessonTeacherId === authUserId) {
    return true
  }

  return false
}

function pickParticipantDisplayName(p: TestParticipant): string {
  return getUserDisplayName(p.user ?? { id: p.user_id })
}

function pickParticipantForResultsGauge(
  run: Test,
  opts: {
    isGroup: boolean
    selectedParticipant: TestParticipant | null
    authUserId: number | null
    isTeacherLike: boolean
  },
): TestParticipant | null {
  const parts = run.participants ?? []
  if (parts.length === 0) return null
  if (opts.isGroup && opts.selectedParticipant) return opts.selectedParticipant
  if (opts.isTeacherLike && !opts.isGroup && opts.selectedParticipant) return opts.selectedParticipant
  const targetUid = testTargetUserId(run)
  if (targetUid != null) {
    return parts.find((p) => (p.user?.id ?? p.user_id) === targetUid) ?? null
  }
  if (!opts.isTeacherLike && opts.authUserId != null) {
    return parts.find((p) => (p.user?.id ?? p.user_id) === opts.authUserId) ?? null
  }
  return parts.find((p) => p.results_score != null) ?? parts[0] ?? null
}

type TestResultsBlockPayload = {
  answers_total: number
  answers_checked: number
  answers_correct: number
  answers_incorrect: number
}

function TestResultsSummaryWithGauge({
  results,
  participant,
  t,
  wrap,
}: {
  results: TestResultsBlockPayload
  participant: TestParticipant | null
  t: TFunction
  wrap: 'card' | 'paper'
}) {
  const inner = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" component="h2" fontWeight={700} sx={{ mb: 0.5 }}>
          {t('tests.results.title')}
        </Typography>
        <Typography variant="body2">
          {t('tests.results.total')}: {results.answers_total}
        </Typography>
        <Typography variant="body2">
          {t('tests.results.correct')}: {results.answers_correct}
        </Typography>
        <Typography variant="body2">
          {t('tests.results.incorrect')}: {results.answers_incorrect}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('tests.results.checked')}: {results.answers_checked}
        </Typography>
      </Stack>
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          alignSelf: { xs: 'center', sm: 'flex-start' },
          pt: { xs: 1, sm: 0 },
          pl: { xs: 0, sm: 1 },
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, textAlign: 'center' }}>
          {t('tests.results.scoreLabel')}
        </Typography>
        <TestScoreGauge score={participant?.results_score} titleUnknown={t('tests.results.scoreUnknown')} />
      </Box>
    </Stack>
  )
  if (wrap === 'card') {
    return (
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>{inner}</CardContent>
      </Card>
    )
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {inner}
    </Paper>
  )
}

function isItemDoneForProgress(item: TestItem): boolean {
  if (item.status === 'completed' || item.status === 'skipped') return true
  if (item.status !== 'active') return false
  const answers = item.answers ?? []
  if (answers.length === 0) return false
  return answers.every((a) => a.is_correct != null)
}

function computeParticipantProgress(run: Test, participantId: number): { done: number; total: number } {
  const items = run.items ?? []
  const anyScoped = items.some((i) => i.participant_id != null)
  const scopeItems = anyScoped ? items.filter((i) => i.participant_id === participantId) : items
  const total = scopeItems.length
  const done = scopeItems.filter(isItemDoneForProgress).length
  return { done, total }
}

function itemsScopedForParticipant(run: Test | null, participantId: number | null): TestItem[] {
  if (!run?.items?.length || participantId == null) return []
  const items = run.items
  const anyScoped = items.some((i) => i.participant_id != null)
  const list = anyScoped ? items.filter((i) => i.participant_id === participantId) : items
  return [...list].sort((a, b) => a.order - b.order)
}

/** Момент открытия позиции в мс; без отметки (или с битой) — раньше любой отмеченной. */
function itemOpenedAtMs(item: TestItem): number {
  const ms = item.opened_at ? Date.parse(item.opened_at) : Number.NaN
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY
}

/**
 * Карточка, которую учитель видит в прогоне участника.
 *
 * После вердикта сервер переводит карточку в `completed`, а следующую не
 * открывает, пока учитель не нажмёт «Следующая», — активной в этот момент нет.
 * Первая `pending` здесь — ещё не открытая N+1: после перезагрузки страницы
 * учитель попадал на неё, а ученик оставался на N. Поэтому порядок такой:
 * активная → последняя открытая из завершённых и пропущенных (по `opened_at`,
 * затем по `order`) → первая ещё не открытая. Ученик при обычном ходе теста
 * видит ту же карточку (`resolveStudentDisplayItemTeacherDriven`).
 *
 * Карточка выводится из прогона, а не хранится на странице: так она одинакова
 * после перезагрузки, при смене ученика в групповом тесте и во второй вкладке.
 */
function resolveTeacherDisplayItem(items: TestItem[]): TestItem | null {
  const byOrder = [...items].sort((a, b) => a.order - b.order)
  const active = byOrder.find((i) => i.status === 'active')
  if (active) return active
  let lastOpened: TestItem | null = null
  for (const item of byOrder) {
    if (item.status !== 'completed' && item.status !== 'skipped') continue
    // byOrder отсортирован по order, поэтому при равном opened_at побеждает
    // более поздняя карточка.
    if (lastOpened === null || itemOpenedAtMs(item) >= itemOpenedAtMs(lastOpened)) {
      lastOpened = item
    }
  }
  if (lastOpened) return lastOpened
  return byOrder.find((i) => i.status === 'pending') ?? null
}

function itemsHaveParticipantScope(run: Test | null): boolean {
  return (run?.items ?? []).some((i) => i.participant_id != null)
}

/** Тест из урока или с разбиением по участникам: активную карточку задаёт учитель, POST next-item не вызываем. */
function runUsesTeacherDrivenItems(run: Test | null): boolean {
  if (!run) return false
  if (run.lesson_id != null) return true
  return itemsHaveParticipantScope(run)
}

function studentScopedItems(run: Test | null, authUserId: number | null): TestItem[] {
  if (!run?.items?.length) return []
  const items = run.items
  if (!itemsHaveParticipantScope(run)) {
    return [...items].sort((a, b) => a.order - b.order)
  }
  const p = (run.participants ?? []).find((x) => (x.user?.id ?? x.user_id) === authUserId)
  if (!p) return []
  return items.filter((i) => i.participant_id === p.id).sort((a, b) => a.order - b.order)
}

/**
 * Позиции прогона, карточки которых показывает эта страница: учителю — все,
 * ученику — только свои.
 */
function runItemsVisibleToViewer(run: Test, authUserId: number | null): TestItem[] {
  if (resolveIsTeacherViewInTest(run, authUserId)) return run.items ?? []
  return studentScopedItems(run, authUserId)
}

/** Тот же набор подсказок в том же порядке — список перерисовывать незачем. */
function sameClueIds(a: Clue[] | undefined, b: Clue[] | undefined): boolean {
  const left = a ?? []
  const right = b ?? []
  return left.length === right.length && left.every((clue, idx) => clue.id === right[idx].id)
}

/**
 * Урок / teacher-driven: показываем active; иначе «залипание» на текущей карточке (в т.ч. completed),
 * пока учитель не откроет следующую; при холодной загрузке — последняя completed по order.
 */
function resolveStudentDisplayItemTeacherDriven(
  run: Test,
  authUserId: number | null,
  prevFocusId: number | null,
): { item: TestItem | null; nextFocusId: number | null } {
  const list = studentScopedItems(run, authUserId)
  if (list.length === 0) return { item: null, nextFocusId: null }
  const active = list.find((i) => i.status === 'active')
  if (active) return { item: active, nextFocusId: active.id }
  if (prevFocusId != null) {
    const hit = list.find((i) => i.id === prevFocusId)
    if (hit) return { item: hit, nextFocusId: prevFocusId }
  }
  const completed = list.filter((i) => i.status === 'completed').sort((a, b) => b.order - a.order)
  if (completed.length) {
    const last = completed[0]
    return { item: last, nextFocusId: last.id }
  }
  return { item: null, nextFocusId: null }
}

function findAnswerForEvaluatedSkill(item: TestItem, skillKey: string): TestAnswer | undefined {
  const target = normalizeSkillType(skillKey)
  const answers = item.answers ?? []
  return answers.find((a) => normalizeSkillType(a.skill_type) === target)
}

function studentItemIndicatorColor(item: TestItem, isCurrent: boolean, theme: Theme): string {
  if (isCurrent || item.status === 'active') return theme.palette.primary.main

  if (item.status !== 'completed') {
    return alpha(theme.palette.text.primary, 0.22)
  }

  const usedHelp = Boolean(item.hint_used || item.transcription_used || item.audio_played)
  const answers = item.answers ?? []
  if (answers.length === 0) return usedHelp ? theme.palette.warning.main : alpha(theme.palette.text.primary, 0.22)

  const pending = answers.some((a) => a.is_correct == null)
  if (pending) return theme.palette.warning.main

  const allCorrect = answers.every((a) => a.is_correct === true)
  if (allCorrect) return usedHelp ? theme.palette.warning.main : theme.palette.success.main

  const allWrong = answers.every((a) => a.is_correct === false)
  if (allWrong) return theme.palette.error.main

  return theme.palette.warning.main
}

function chipSxForTeacherEvalSkill(
  item: Pick<TestItem, 'hint_used' | 'transcription_used'>,
  skillKey: string,
  answer: TestAnswer | undefined,
  theme: Theme,
): Record<string, unknown> {
  const pending = !answer || answer.is_correct == null
  const ok = answer?.is_correct === true
  const skill = normalizeSkillType(skillKey)
  const usedHelpForSkill =
    (skill === 'translation' && Boolean(item.hint_used)) ||
    (skill === 'pronunciation' && Boolean(item.transcription_used))

  // Product rule: if student used help for this goal, max score is reduced -> show as yellow even when correct.
  const forceWarning = usedHelpForSkill && ok

  const border = pending
    ? alpha(theme.palette.text.primary, 0.35)
    : forceWarning
      ? theme.palette.warning.main
      : ok
      ? theme.palette.success.main
      : theme.palette.error.main

  const fg = pending
    ? theme.palette.text.secondary
    : forceWarning
      ? theme.palette.warning.main
      : ok
      ? theme.palette.success.main
      : theme.palette.error.main

  const bg = pending
    ? 'transparent'
    : alpha(
        forceWarning ? theme.palette.warning.main : ok ? theme.palette.success.main : theme.palette.error.main,
        theme.palette.mode === 'light' ? 0.08 : 0.14,
      )

  return {
    '&.MuiChip-root': {
      borderColor: border,
      color: fg,
      backgroundColor: bg,
    },
    '& .MuiChip-label': {
      color: fg,
    },
  }
}

function skillDotColor(
  item: Pick<TestItem, 'hint_used' | 'transcription_used'>,
  skillKey: string,
  answer: TestAnswer | undefined,
  theme: Theme,
): string {
  const pending = !answer || answer.is_correct == null
  const ok = answer?.is_correct === true
  const skill = normalizeSkillType(skillKey)
  const usedHelpForSkill =
    (skill === 'translation' && Boolean(item.hint_used)) ||
    (skill === 'pronunciation' && Boolean(item.transcription_used))
  const forceWarning = usedHelpForSkill && ok

  if (pending) return alpha(theme.palette.text.primary, 0.35)
  if (forceWarning) return theme.palette.warning.main
  if (ok) return theme.palette.success.main
  return theme.palette.error.main
}

function mergeTestItemPreserveCard(prev: TestItem | null, next: TestItem | null): TestItem | null {
  if (!next) return null
  if (prev && prev.id !== next.id) return next
  if (prev && prev.id === next.id && next.card == null && prev.card != null) {
    return { ...next, card: prev.card }
  }
  return next
}

function runHasIncompleteItems(run: Test, participantId: number | null): boolean {
  const items = run.items ?? []
  const anyScoped = items.some((i) => i.participant_id != null)
  const scopeItems =
    anyScoped && participantId != null
      ? items.filter((i) => i.participant_id === participantId)
      : items
  return scopeItems.some((item) => {
    if (item.status === 'pending') return true
    if (item.status !== 'active') return false
    const answers = item.answers ?? []
    if (answers.length === 0) return true
    return answers.some((a) => a.is_correct == null)
  })
}

function formatDurationMs(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${hours > 0 ? `${pad(hours)}:` : ''}${pad(minutes)}:${pad(seconds)}`
}

function formatClockTimeHHmm(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const loc = locale.toLowerCase().startsWith('ru') ? 'ru-RU' : 'en-US'
  return d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' })
}

function getWordAttr(word: CardModel['word'] | undefined, key: string): string {
  const a = word?.attributes?.find((x) => x.key === key)
  const v = a?.value?.trim()
  return v ?? ''
}

/**
 * Первая подсказка карточки — та, что берёт бэкенд.
 *
 * `TestAnswerAutoCheckService::expectedTranslation` на слове без атрибута
 * перевода берёт подсказку через `orderBy('id')`, и SPA обязана выбирать ту же:
 * она же показывается строкой перевода. Порядок массива с сервера уже
 * отсортирован (`Card::clues()`), но локально добавленные подсказки дописываются
 * в конец, поэтому считаем минимальный id явно.
 */
function primaryClue(card: CardModel | undefined): Clue | null {
  const clues = card?.clues
  if (!clues?.length) return null
  return clues.reduce((min, clue) => (clue.id < min.id ? clue : min), clues[0])
}

/**
 * Подсказка, которую нельзя удалять из панели.
 *
 * Пока у слова нет атрибута перевода, первая подсказка одновременно и «перевод»
 * на экране, и эталон автопроверки на сервере: удаление посреди прогона
 * показало бы ученику прочерк вместо задания и переписало бы правильный ответ.
 */
function protectedClueId(card: CardModel | undefined): number | null {
  if (!card || getWordAttr(card.word, 'translation')) return null
  return primaryClue(card)?.id ?? null
}

/** Правка подсказок карточки: добавление либо удаление одной подсказки. */
type ClueOp = { cardId: number; add: Clue } | { cardId: number; removeId: number }

function applyClueOpsToCard(card: CardModel, ops: ClueOp[]): CardModel {
  const own = ops.filter((op) => op.cardId === card.id)
  if (!own.length) return card
  let clues = card.clues ?? []
  let changed = false
  for (const op of own) {
    if ('removeId' in op) {
      if (!clues.some((c) => c.id === op.removeId)) continue
      clues = clues.filter((c) => c.id !== op.removeId)
      changed = true
    } else if (!clues.some((c) => c.id === op.add.id)) {
      clues = [...clues, op.add]
      changed = true
    }
  }
  return changed ? { ...card, clues } : card
}

function applyClueOpsToItems(items: TestItem[] | undefined, ops: ClueOp[]): TestItem[] | undefined {
  if (!items?.length || !ops.length) return items
  let changed = false
  const next = items.map((it) => {
    if (!it.card) return it
    const card = applyClueOpsToCard(it.card, ops)
    if (card === it.card) return it
    changed = true
    return { ...it, card }
  })
  return changed ? next : items
}

function cardWordSubtitle(card: CardModel | undefined): string {
  if (!card) return ''
  const clue = primaryClue(card)?.text?.trim()
  if (clue) return clue
  return getWordAttr(card.word, 'translation')
}

/** Только перевод из атрибутов слова (без подсказки-клю), для строки результатов. */
function cardWordTranslationOnly(card: CardModel | undefined): string {
  if (!card) return ''
  return getWordAttr(card.word, 'translation').trim()
}

function cardWordPrimaryText(card: CardModel | undefined, cardId: number, t: TFunction): string {
  return (card?.word?.text && card.word.text.trim()) || t('tests.run.fallbackCard', { id: cardId })
}

function formatRunDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const loc = locale.toLowerCase().startsWith('ru') ? 'ru-RU' : 'en-US'
  return d.toLocaleString(loc, {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}

function answerPayloadText(answer: TestAnswer | undefined): string {
  if (!answer) return ''
  const payload = answer.answer_payload
  if (payload && typeof payload === 'object' && 'text' in payload && typeof (payload as { text: unknown }).text === 'string') {
    return (payload as { text: string }).text.trim()
  }
  return ''
}

/** Строки ответа, по которым учитель ещё должен выставить верно/неверно. */
function answersPendingTeacherEvaluation(answers: TestAnswer[] | undefined): TestAnswer[] {
  return (answers ?? []).filter(
    (a) => a.is_correct == null && !a.unverifiable_at && (a.checked_by == null || a.checked_by === 'teacher'),
  )
}

/** Ответ, который платформе нечем проверить: у карточки не оказалось эталона. */
function isAnswerUnverifiable(answer: TestAnswer | undefined): boolean {
  return Boolean(answer && answer.is_correct == null && answer.unverifiable_at)
}

function testAutoCheckEnabled(run: Test | null | undefined): boolean {
  const snap = run?.settings_snapshot
  if (snap && typeof snap.auto_check_enabled === 'boolean') return Boolean(snap.auto_check_enabled)
  return true
}

/** Для карточки возможна автопроверка текста (настройка теста + тип ответа). */
function itemAutoCheckApplies(run: Test | null | undefined, item: TestItem): boolean {
  return testAutoCheckEnabled(run) && answerTypeSupportsAutoChecking(item.answer_type)
}

function formatAnswerResult(last: TestAnswer | undefined, t: TFunction): string {
  if (!last) return '—'
  if (last.is_correct === true) return t('tests.teacher.correct')
  if (last.is_correct === false) return t('tests.teacher.incorrect')
  // Ждать вердикта незачем: проверить этот ответ платформе нечем, а учителя в
  // прогоне нет — «На проверке» висело бы вечно.
  if (isAnswerUnverifiable(last)) return t('tests.run.notVerifiable')
  return t('tests.run.awaitingReview')
}

/** Сырые баллы теста (+2, +1, 0, −1, −2) для подписи */
function formatTestProgressStep(step: number): string {
  if (step > 0) return `+${step}`
  return String(step)
}

function formatAxisDelta(delta: number): string {
  if (delta > 0) return `+${delta}`
  return String(delta)
}

function progressStepSx(step: number): SxProps<Theme> {
  return (theme: Theme) => ({
    fontWeight: 700,
    color:
      step > 0 ? theme.palette.success.main : step < 0 ? theme.palette.error.main : theme.palette.text.secondary,
  })
}

/** Цвет для фактической дельты по шкале 0–100 (когда нет целого «шага» +2/+1 и т.д.) */
function progressAxisDeltaSx(delta: number): SxProps<Theme> {
  return (theme: Theme) => ({
    fontWeight: 700,
    color:
      delta > 0 ? theme.palette.success.main : delta < 0 ? theme.palette.error.main : theme.palette.text.secondary,
  })
}

function skillCheckLabel(skill: string | null | undefined, t: TFunction): string {
  if (!skill) return '—'
  return t(`tests.run.skillLabels.${skill}`, { defaultValue: skill })
}

/** Навыки, проверяемые по заданию: `active_evaluated_skill_types`, иначе основной `skill_type`. */
function itemEvaluatedSkillList(
  item: Pick<TestItem, 'active_evaluated_skill_types' | 'skill_type'>,
): string[] {
  const active = item.active_evaluated_skill_types
  if (Array.isArray(active) && active.length > 0) {
    return [...new Set(active.map((s) => String(s).trim()).filter(Boolean))]
  }
  const s = item.skill_type?.trim()
  return s ? [s] : []
}

function skillChipShortLabel(skill: string, t: TFunction): string {
  const s = normalizeSkillType(skill)
  const map: Record<string, string> = {
    pronunciation: t('tests.run.skillLabels.pronunciation', { defaultValue: 'произн' }),
    translation: t('tests.run.skillLabels.translation', { defaultValue: 'перевод' }),
    reverse_translation: t('tests.run.skillLabels.reverse_translation', { defaultValue: 'оригинал' }),
    spelling: t('tests.run.skillLabels.spelling', { defaultValue: 'правописание' }),
    listening: t('tests.run.skillLabels.listening', { defaultValue: 'слух' }),
  }
  return map[s] ?? t(`tests.run.skillLabels.${s}`, { defaultValue: s })
}

// (was used by old teacher card list; no longer needed)

function promptTypeHuman(pt: string, t: TFunction): string {
  return t(`tests.run.promptTypes.${pt}`, { defaultValue: pt })
}

function answerTypeShortLabel(answerType: string, t: TFunction): string {
  return t(`tests.run.answerTypeLabels.${answerType}`, { defaultValue: answerType })
}

/** Бейджи: отображение (глаз) → проверка (мишень) → ответ (облачко) → [auto] при авто-проверке. */
function TestItemContextBadges({
  item,
  t,
  dense,
}: {
  item: Pick<TestItem, 'prompt_type' | 'skill_type' | 'answer_type' | 'active_evaluated_skill_types'>
  t: TFunction
  dense?: boolean
}) {
  const iconSx = { fontSize: dense ? 16 : 18 }
  const chipSx = dense
    ? {
        height: 24,
        maxWidth: 148,
        '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis' },
        '& .MuiChip-icon': { ml: 0.5 },
      }
    : {
        maxWidth: 220,
        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
      }

  const showAuto = answerTypeSupportsAutoChecking(item.answer_type)
  const skillKeys = itemEvaluatedSkillList(item)

  return (
    <>
      <Tooltip title={t('tests.run.badges.tooltipDisplay', { value: promptTypeHuman(item.prompt_type, t) })}>
        <Chip
          size="small"
          variant="outlined"
          icon={<VisibilityOutlined sx={iconSx} />}
          label={promptTypeHuman(item.prompt_type, t)}
          sx={chipSx}
        />
      </Tooltip>
      {skillKeys.length === 0 ? (
        <Tooltip title={t('tests.run.badges.tooltipSkill', { value: '—' })}>
          <Chip size="small" variant="outlined" icon={<Adjust sx={iconSx} />} label="—" sx={chipSx} />
        </Tooltip>
      ) : (
        skillKeys.map((sk, idx) => (
          <Tooltip key={`${sk}-${idx}`} title={t('tests.run.badges.tooltipSkill', { value: skillCheckLabel(sk, t) })}>
            <Chip
              size="small"
              variant="outlined"
              icon={<Adjust sx={iconSx} />}
              label={skillCheckLabel(sk, t)}
              sx={chipSx}
            />
          </Tooltip>
        ))
      )}
      {item.answer_type ? (
        <Tooltip
          title={t('tests.run.badges.tooltipAnswer', { value: answerTypeShortLabel(item.answer_type, t) })}
        >
          <Chip
            size="small"
            variant="outlined"
            icon={<ChatBubbleOutline sx={iconSx} />}
            label={answerTypeShortLabel(item.answer_type, t)}
            sx={chipSx}
          />
        </Tooltip>
      ) : null}
      {showAuto ? (
        <Tooltip title={t('tests.run.badges.autoTooltip')}>
          <Chip
            size="small"
            label={t('tests.run.badges.auto')}
            color="success"
            variant="outlined"
            sx={dense ? { height: 24, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } } : {}}
          />
        </Tooltip>
      ) : null}
    </>
  )
}

/**
 * Строка перевода: атрибут слова, иначе — первая подсказка.
 *
 * Подсказку, подставленную сюда, из списка больше не вырезаем: раньше на слове
 * без атрибута перевода первая подсказка исчезала из панели навсегда, и «Найти
 * у других» предлагала её как новую. Но и удалять её нельзя — на сервере она
 * работает эталоном автопроверки (`TestAnswerAutoCheckService`), поэтому в
 * панели она помечена и без корзины (`protectedClueId`).
 */
function cardTranslationLine(card: CardModel | undefined): string {
  if (!card) return '—'
  const fromAttr = getWordAttr(card.word, 'translation')
  if (fromAttr) return fromAttr
  return primaryClue(card)?.text?.trim() || '—'
}

/** Чип «подсказка»: матрица допустимости + флаги item (как на бэкенде для hint_opened). */
// (was used for teacher “used help” chips; removed per new UX)

function TeacherLessonActiveCardFocus({
  item,
  t,
  evaluatingAnswerIds,
  advanceNextSubmitting,
  onTeacherEvaluate,
  advanceNextDisabled,
  onAdvanceNextCard,
  testCompleted,
  autoCheckOnThisItem,
  itemIsOral,
  studentAnswerDraftPreview,
  advanceNextTooltip,
  hasMoreCards,
  waitingForTeacherStart,
  onCardClueAdded,
  onCardClueDeleted,
}: {
  item: TestItem | null
  t: TFunction
  evaluatingAnswerIds: Record<number, true>
  advanceNextSubmitting: boolean
  onTeacherEvaluate: (itemId: number, answerId: number, isCorrect: boolean) => void
  advanceNextDisabled: boolean
  onAdvanceNextCard: () => void
  testCompleted: boolean
  /** Есть ли для этой карточки автопроверка (влияет на текст, пока ответа ещё нет). */
  autoCheckOnThisItem: boolean
  /** Тип ответа speech_* — ученик не шлёт текст в API. */
  itemIsOral: boolean
  /** Черновик ответа ученика (realtime). */
  studentAnswerDraftPreview: string | null
  advanceNextTooltip: string
  hasMoreCards: boolean
  /** Прогон ещё в черновике: первая карточка не показывается до «Запустить тест». */
  waitingForTeacherStart: boolean
  /** Учитель добавил подсказку в панели — обновить прогон на месте. */
  onCardClueAdded: (cardId: number, clue: Clue) => void
  /** Учитель удалил подсказку в панели — обновить прогон на месте. */
  onCardClueDeleted: (cardId: number, clueId: number) => void
}) {
  // Первая неоценённая строка проверки — адресат клавиш 1 и 2. Считается до
  // раннего return: хук обязан вызываться на каждом рендере.
  const hotkeyAnswer = (item?.answers ?? []).find(
    (answer) => answer.is_correct == null && !evaluatingAnswerIds[answer.id],
  )
  const hotkeyAnswerId = hotkeyAnswer?.id ?? null
  const hotkeysActive = Boolean(item) && !testCompleted && !waitingForTeacherStart
  const canAdvanceByHotkey = !testCompleted && !advanceNextSubmitting && !advanceNextDisabled && hasMoreCards

  useEffect(() => {
    if (!hotkeysActive) return

    const handler = (event: KeyboardEvent) => {
      // В поле ввода цифры печатаются, а не оценивают.
      if (isTypingTarget(event.target)) return
      // Сочетания с модификаторами принадлежат браузеру и ОС, автоповтор при
      // зажатой клавише — не намерение учителя.
      if (event.ctrlKey || event.metaKey || event.altKey || event.repeat) return
      // Enter на сфокусированной кнопке или ссылке обязан нажать именно её:
      // isTypingTarget по построению пропускает такие элементы.
      const active = document.activeElement
      if (active instanceof HTMLElement && active.closest('button, a[href], [role="button"], [role="option"]')) {
        return
      }

      if (event.key === 'Enter') {
        if (!canAdvanceByHotkey) return
        event.preventDefault()
        onAdvanceNextCard()

        return
      }

      if (event.key !== '1' && event.key !== '2') return
      // Пока строка в процессе оценки, адресата нет — второе нажатие не должно
      // отправлять вердикт повторно.
      if (hotkeyAnswerId === null || item === null) return

      event.preventDefault()
      onTeacherEvaluate(item.id, hotkeyAnswerId, event.key === '1')
    }

    window.addEventListener('keydown', handler)

    return () => window.removeEventListener('keydown', handler)
  }, [
    hotkeysActive,
    canAdvanceByHotkey,
    hotkeyAnswerId,
    item,
    onAdvanceNextCard,
    onTeacherEvaluate,
  ])

  if (!item) {
    return (
      <Card sx={{ borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
        <CardContent sx={{ py: 4 }}>
          <Typography color="text.secondary" textAlign="center">
            {waitingForTeacherStart
              ? t('tests.teacher.individual.startTestPrompt')
              : t('tests.teacher.individual.noActiveCard')}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  const card = item.card
  const wordText =
    (card?.word?.text && card.word.text.trim()) || t('tests.run.fallbackCard', { id: item.card_id })
  const transcription = getWordAttr(card?.word, 'transcription')
  const translation = cardTranslationLine(card)
  const answers = item.answers ?? []

  return (
    <Card
      sx={(theme) => ({
        borderRadius: 3,
        border: `2px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.4 : 0.55)}`,
        boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, theme.palette.mode === 'light' ? 0.05 : 0.14)}`,
      })}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2.5}>
          <Typography
            variant="h3"
            component="div"
            fontWeight={800}
            sx={{
              fontSize: { xs: '2rem', sm: '2.85rem' },
              lineHeight: 1.12,
              wordBreak: 'break-word',
              letterSpacing: '-0.02em',
            }}
          >
            {wordText}
          </Typography>

          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            <TestItemContextBadges item={item} t={t} />
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.06 }}>
              {t('tests.run.focusCard.transcription')}
            </Typography>
            <Typography variant="h6" component="div" sx={{ mt: 0.5, fontWeight: 600, wordBreak: 'break-word' }}>
              {transcription ? `[${transcription}]` : '—'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.06 }}>
              {t('tests.run.focusCard.translation')}
            </Typography>
            <Typography variant="h6" component="div" sx={{ mt: 0.5, fontWeight: 600, wordBreak: 'break-word' }}>
              {translation}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              {t('tests.teacher.individual.evaluateTitle')}
            </Typography>
            {studentAnswerDraftPreview ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                {t('tests.run.studentAnswer')}: {studentAnswerDraftPreview}
              </Typography>
            ) : null}
            {answers.length === 0 ? (
              itemIsOral ? (
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  {t('tests.teacher.individual.waitingOralTeacherGrades')}
                </Typography>
              ) : autoCheckOnThisItem ? (
                <Typography variant="body2" color="text.secondary">
                  {t('tests.teacher.individual.waitingStudentAnswerAuto')}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('tests.teacher.individual.waitingStudentAnswerManual')}
                </Typography>
              )
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    '& .MuiTableCell-root': { py: 0.5, verticalAlign: 'middle' },
                    '& .MuiTableCell-head': { py: 0.6, fontWeight: 700 },
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 180 }}>{t('tests.teacher.individual.checkColumn')}</TableCell>
                        <TableCell>{t('tests.teacher.individual.answerColumn')}</TableCell>
                        <TableCell align="right" sx={{ width: 240 }}>
                          {t('tests.teacher.individual.actionsColumn')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {answers.map((answer) => {
                        const text = answerPayloadText(answer)
                        const hasVerdict = answer.is_correct != null
                        const rowBusy = Boolean(evaluatingAnswerIds[answer.id])
                        // Адресат горячих клавиш — первая неоценённая строка.
                        const isHotkeyTarget = !testCompleted && answer.id === hotkeyAnswerId

                        const rowTone = answer.is_correct === true ? 'success' : answer.is_correct === false ? 'error' : null

                        const sk = normalizeSkillType(answer.skill_type ?? '')
                        const showUsedTranscription = sk === 'pronunciation' && Boolean(item.transcription_used)
                        const showUsedHint =
                          (sk === 'translation' || sk === 'reverse_translation') && Boolean(item.hint_used)

                        return (
                          <TableRow
                            key={answer.id}
                            hover
                            sx={(theme) => {
                              const outline = isHotkeyTarget
                                ? { outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`, outlineOffset: '-2px' }
                                : {}
                              if (!rowTone) return outline
                              const base = rowTone === 'success' ? theme.palette.success.main : theme.palette.error.main
                              return {
                                ...outline,
                                backgroundColor: alpha(base, theme.palette.mode === 'light' ? 0.06 : 0.12),
                                '&:hover': {
                                  backgroundColor: alpha(base, theme.palette.mode === 'light' ? 0.10 : 0.18),
                                },
                              }
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {skillCheckLabel(answer.skill_type, t)}
                              </Typography>
                              {showUsedTranscription ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.2 }}>
                                  {t('tests.run.studentUsed.transcription')}
                                </Typography>
                              ) : null}
                              {showUsedHint ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.2 }}>
                                  {t('tests.run.studentUsed.hint')}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {text || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  size="small"
                                  variant="contained"
                                  sx={{
                                    textTransform: 'none',
                                    minWidth: 72,
                                    px: 0.75,
                                    py: 0,
                                    minHeight: 28,
                                    height: 28,
                                    lineHeight: 1.1,
                                    fontSize: '0.75rem',
                                    opacity: hasVerdict && answer.is_correct === false ? 0.45 : 1,
                                  }}
                                  disabled={testCompleted || rowBusy}
                                  onClick={() => onTeacherEvaluate(item.id, answer.id, true)}
                                >
                                  {t('tests.teacher.correct')}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    textTransform: 'none',
                                    minWidth: 72,
                                    px: 0.75,
                                    py: 0,
                                    minHeight: 28,
                                    height: 28,
                                    lineHeight: 1.1,
                                    fontSize: '0.75rem',
                                    opacity: hasVerdict && answer.is_correct === true ? 0.45 : 1,
                                  }}
                                  disabled={testCompleted || rowBusy}
                                  onClick={() => onTeacherEvaluate(item.id, answer.id, false)}
                                >
                                  {t('tests.teacher.incorrect')}
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {!testCompleted ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.45 }}>
                      {t('tests.teacher.individual.overrideGradeHint')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.45 }}>
                      {t('tests.teacher.individual.hotkeysHint')}
                    </Typography>
                  </>
                ) : null}
              </>
            )}
          </Box>

          <Divider />

          {card ? (
            <CardClues
              key={card.id}
              cardId={card.id}
              wordId={card.word_id}
              clues={card.clues ?? []}
              protectedClueId={protectedClueId(card)}
              protectedClueNote={t('cards.messages.clue_used_as_translation_teacher')}
              onClueAdded={(clue) => onCardClueAdded(card.id, clue)}
              onClueDeleted={(clueId) => onCardClueDeleted(card.id, clueId)}
            />
          ) : null}

          {!testCompleted && !advanceNextDisabled && hasMoreCards ? (
            <>
              <Divider />
              <Box sx={{ width: '100%' }}>
                <Tooltip title={advanceNextTooltip}>
                  <span style={{ display: 'block', width: '100%' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ textTransform: 'none' }}
                      disabled={advanceNextSubmitting || advanceNextDisabled}
                      onClick={() => onAdvanceNextCard()}
                    >
                      {t('tests.teacher.individual.nextCard')}
                    </Button>
                  </span>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.4 }}>
                  {t('tests.teacher.individual.nextCardHelp')}
                </Typography>
              </Box>
            </>
          ) : !testCompleted && !hasMoreCards ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {t('tests.teacher.individual.noMoreCards', {
                defaultValue: 'Карточки закончились. Завершите тест кнопкой справа.',
              })}
            </Typography>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  )
}

function TestCardFace({
  card,
  cardId,
  t,
}: {
  card?: CardModel | null
  cardId: number
  t: TFunction
}) {
  const title =
    (card?.word?.text && card.word.text.trim()) || t('tests.run.fallbackCard', { id: cardId })
  const sub = cardWordSubtitle(card ?? undefined)
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
        {title}
      </Typography>
      {sub ? (
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
          {sub}
        </Typography>
      ) : null}
    </Stack>
  )
}

/** Строки карточек на экране завершённого теста (без общей обёртки Card): каждая — outlined Paper под блоком сводки. */
function TestCompletedCardsList({ items, t }: { items: TestItem[]; t: TFunction }) {
  const sorted = [...items].sort((a, b) => a.order - b.order)
  if (sorted.length === 0) return null
  return (
    <Stack spacing={1.25}>
      {sorted.map((it) => {
        const word = cardWordPrimaryText(it.card ?? undefined, it.card_id, t)
        const translation = cardWordTranslationOnly(it.card ?? undefined)
        const answers = [...(it.answers ?? [])].sort((a, b) => a.id - b.id)
        return (
          <Paper
            key={it.id}
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
              borderColor: 'divider',
            }}
          >
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, columnGap: 1.5 }}>
                <Typography component="span" variant="h5" fontWeight={700} sx={{ wordBreak: 'break-word', lineHeight: 1.2 }}>
                  {word}
                </Typography>
                {translation ? (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                    {translation}
                  </Typography>
                ) : null}
              </Box>
              <Stack spacing={0.75}>
                {answers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('tests.results.noAnswerRows')}
                  </Typography>
                ) : (
                  answers.map((a) => {
                    const step = a.progress_step
                    const axis = a.progress_axis_delta
                    const skillLabel = skillCheckLabel(a.skill_type, t)
                    return (
                      <Typography key={a.id} variant="body2" sx={{ lineHeight: 1.45 }}>
                        <Box component="span" fontWeight={600}>
                          {skillLabel}
                        </Box>
                        {': '}
                        <Box component="span">{formatAnswerResult(a, t)}</Box>
                        {step != null ? (
                          <>
                            {' · '}
                            <Box component="span" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {t('tests.results.progressNamedStep', { skill: skillLabel })}
                            </Box>
                            <Box component="span" sx={progressStepSx(step)}>
                              {formatTestProgressStep(step)}
                            </Box>
                          </>
                        ) : axis != null && axis !== 0 ? (
                          <>
                            {' · '}
                            <Box component="span" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {t('tests.results.progressNamedStep', { skill: skillLabel })}
                            </Box>
                            <Box component="span" sx={progressAxisDeltaSx(axis)} title={t('tests.results.progressAxisHint')}>
                              {formatAxisDelta(axis)}
                            </Box>
                          </>
                        ) : null}
                      </Typography>
                    )
                  })
                )}
              </Stack>
            </Stack>
          </Paper>
        )
      })}
    </Stack>
  )
}

function extractChoiceOptions(item: TestItem): string[] {
  const raw = (item as unknown as { options?: unknown; choices?: unknown; answer_options?: unknown }).options ??
    (item as unknown as { choices?: unknown }).choices ??
    (item as unknown as { answer_options?: unknown }).answer_options
  if (!Array.isArray(raw)) return []
  return raw.map((x) => (typeof x === 'string' ? x : typeof x === 'number' ? String(x) : '')).map((s) => s.trim()).filter(Boolean)
}

function TestPageBreadcrumbs({
  t,
  run,
  runId,
  isTeacherLike,
  sx,
}: {
  t: TFunction
  run: Test | null
  runId: number
  isTeacherLike: boolean
  sx?: SxProps<Theme>
}) {
  const displayId = run?.id ?? runId

  const lessonCrumbs = useMemo(() => {
    if (run?.lesson_id == null) return null
    return [
      <Link key="crumb-lessons" component={RouterLink} to={ROUTES.LESSONS} underline="hover" color="inherit">
        {t('lessons.title')}
      </Link>,
      <Link
        key="crumb-lesson"
        component={RouterLink}
        to={ROUTES.LESSON_DETAILS(run.lesson_id)}
        underline="hover"
        color="inherit"
      >
        {t('lessons.detailsTitle', { id: run.lesson_id })}
      </Link>,
    ]
  }, [run?.lesson_id, t])

  let middle: ReactNode = null
  if (run && run.lesson_id == null) {
    const tUser = testTargetUserId(run)
    if (isTeacherLike && tUser != null) {
      middle = (
        <Link
          component={RouterLink}
          to={ROUTES.USER_TESTS(tUser)}
          underline="hover"
          color="inherit"
        >
          {t('tests.userTitle')}
        </Link>
      )
    } else if (isTeacherLike && testIsGroup(run)) {
      const gid = testTargetGroupId(run)
      if (gid != null) {
        middle = (
          <Link
            component={RouterLink}
            to={`/groups/${gid}`}
            underline="hover"
            color="inherit"
          >
            {t('tests.run.breadcrumbGroup', { id: gid })}
          </Link>
        )
      }
    }
  }

  return (
    <Breadcrumbs
      separator={<NavigateNextIcon fontSize="small" />}
      aria-label="breadcrumb"
      sx={[{ mb: 2 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      <Link
        component={RouterLink}
        to={ROUTES.DASHBOARD}
        underline="hover"
        color="inherit"
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
        {t('dashboard.home')}
      </Link>
      {lessonCrumbs}
      {!lessonCrumbs && !isTeacherLike ? (
        <Link component={RouterLink} to={ROUTES.TESTS} underline="hover" color="inherit">
          {t('tests.title')}
        </Link>
      ) : null}
      {middle}
      <Typography color="text.primary">{t('tests.run.title', { id: displayId })}</Typography>
    </Breadcrumbs>
  )
}

function TestPageTeacherLoadingSkeleton({ runId, t }: { runId: number; t: TFunction }) {
  return (
    <DashboardLayout>
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          pb: 3,
          maxWidth: 1200,
          mx: 'auto',
          width: '100%',
          minHeight: 'calc(100vh - 120px)',
        }}
      >
        <TestPageBreadcrumbs t={t} run={null} runId={runId} isTeacherLike />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Skeleton variant="rounded" width={96} height={34} />
          <Skeleton variant="text" sx={{ flex: 1, minWidth: 140, maxWidth: 360 }} height={40} />
          <Skeleton variant="rounded" width={110} height={32} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              width: { xs: '100%', lg: 'auto' },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Skeleton variant="text" width="72%" height={52} sx={{ mb: 2 }} />
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                  <Skeleton variant="rounded" width={128} height={30} />
                  <Skeleton variant="rounded" width={148} height={30} />
                </Stack>
                <Skeleton variant="text" width="36%" height={24} />
                <Skeleton variant="text" width="88%" height={28} sx={{ mt: 1 }} />
                <Skeleton variant="text" width="36%" height={24} sx={{ mt: 2 }} />
                <Skeleton variant="text" width="70%" height={28} sx={{ mt: 1 }} />
                <Divider sx={{ my: 2 }} />
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
                  <Skeleton variant="rounded" width={112} height={28} />
                  <Skeleton variant="rounded" width={132} height={28} />
                </Stack>
              </CardContent>
            </Card>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ py: 1.25, px: 1.25 }}>
                <Stack spacing={0.75}>
                  {[0, 1, 2, 3, 4, 5].map((k) => (
                    <Skeleton key={k} variant="rounded" height={38} sx={{ borderRadius: 1.5 }} />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Box>
          <Box
            sx={{
              width: { xs: '100%', lg: 360 },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Skeleton variant="text" width="55%" height={32} sx={{ mb: 1.25 }} />
                {[0, 1, 2, 3, 4].map((k) => (
                  <Box
                    key={k}
                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 1 }}
                  >
                    <Skeleton width="42%" height={22} />
                    <Skeleton width="28%" height={22} />
                  </Box>
                ))}
                <Skeleton variant="rounded" width={88} height={30} sx={{ mt: 0.5 }} />
              </CardContent>
            </Card>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
                {[0, 1, 2].map((k) => (
                  <Skeleton key={k} variant="rounded" height={58} sx={{ mb: 1, borderRadius: 2 }} />
                ))}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </DashboardLayout>
  )
}

function TestPageStudentLoadingSkeleton() {
  return (
    <Box sx={{ flex: 1, p: 2, overflow: 'auto', width: '100%', minWidth: 0 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Skeleton variant="text" width="38%" height={36} sx={{ mb: 1.25 }} />
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Skeleton variant="rounded" width={76} height={26} />
          <Skeleton variant="rounded" width={104} height={26} />
          <Skeleton variant="rounded" width={92} height={26} />
        </Stack>
        <Skeleton variant="text" width="52%" sx={{ mt: 1.5 }} />
        <Skeleton variant="text" width="40%" />
      </Paper>
      <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2, mb: 2 }} />
      <Skeleton variant="text" width="48%" height={28} />
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="76%" />
    </Box>
  )
}

export function TestPage() {
  const { t, i18n } = useTranslation()
  const { testId } = useParams()
  const id = useMemo(() => Number(testId), [testId])

  const authUser = useAuthStore((s) => s.user)
  const authUserId = authUser?.id ?? null

  const [run, setRun] = useState<Test | null>(null)
  const [results, setResults] = useState<null | { answers_total: number; answers_checked: number; answers_correct: number; answers_incorrect: number }>(null)
  const [activeItem, setActiveItem] = useState<TestItem | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [answerText, setAnswerText] = useState('')
  const [studentSelectedChoice, setStudentSelectedChoice] = useState<string | null>(null)
  const [studentAnsweredLocal, setStudentAnsweredLocal] = useState(false)
  /**
   * Вердикт автопроверки вместе с карточкой, к которой он относится.
   *
   * Без привязки к позиции его гасил ресет-эффект: ответ ученика меняет состав
   * ответов активной карточки, а `id` в прогоне учителя остаётся прежним, и
   * React 18 сводит установку вердикта и перерасчёт зависимостей в один коммит.
   */
  const [autoCheckFeedback, setAutoCheckFeedback] = useState<{
    itemId: number
    verdict: 'correct' | 'incorrect'
  } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [completingRun, setCompletingRun] = useState(false)
  const [studentTranscriptionOpen, setStudentTranscriptionOpen] = useState(false)
  const [studentCluesOpen, setStudentCluesOpen] = useState(false)
  const [studentDraftByItemId, setStudentDraftByItemId] = useState<Record<number, { user_id: number; text_preview: string }>>({})

  const isCompleted = run?.status === 'completed'
  const isGroup = run != null && testIsGroup(run)

  /**
   * Прогон закрылся вместе с уроком: бэкенд финализирует активные lesson-тесты
   * в LessonLifecycleService. Отличаем от обычного завершения по статусу урока,
   * чтобы объяснить ученику, почему тест закончился без его действий.
   */
  const closedWithLesson = useMemo<'completed' | 'cancelled' | null>(() => {
    if (!run || run.lesson_id == null) return null
    if (run.status === 'cancelled' && run.lesson?.status === 'canceled') return 'cancelled'
    if (run.status === 'completed' && run.lesson?.status === 'completed') return 'completed'
    return null
  }, [run])

  /**
   * Тест урока закрыт до его начала. `available_at` — момент открытия
   * (planned_start_at минус окно подготовки); показываем время ученику,
   * чтобы он понимал, когда возвращаться.
   */
  const notStartedNotice = useMemo<string | null>(() => {
    if (!run || run.can_interact !== false) return null
    if (!run.available_at) return t('tests.run.lessonNotStarted')
    return t('tests.run.lessonNotStartedAt', {
      time: formatDateTimeDMY(run.available_at),
    })
  }, [run, t])

  const isTeacherLike = useMemo(
    () => resolveIsTeacherViewInTest(run, authUserId),
    [run, authUserId],
  )

  const [selectedParticipantId, setSelectedParticipantId] = useState<number | null>(null)
  /** Чтобы `reload` не зависел от выбранного участника — иначе смена ученика в групповом тесте дублирует GET /tests/:id. */
  const selectedParticipantIdRef = useRef<number | null>(null)
  useEffect(() => {
    selectedParticipantIdRef.current = selectedParticipantId
  }, [selectedParticipantId])

  /** Ученик: удерживаем отображаемую карточку (в т.ч. completed) между reload и без лишних вызовов POST next-item. */
  const studentFocusRef = useRef<number | null>(null)
  /** Один таймер черновика на всю страницу: его гасит и смена карточки, и отправка ответа. */
  const draftTimerRef = useRef({
    id: null as number | null,
    set(timerId: number) {
      this.id = timerId
    },
    cancel() {
      if (this.id != null) {
        window.clearTimeout(this.id)
        this.id = null
      }
    },
  })
  /**
   * Идущее продвижение вместе с якорем, от которого оно стартовало.
   *
   * Дедуп «есть запрос в полёте» склеивал только строго одновременные вызовы, а
   * продвижение зовут из двух мест — после ответа и из realtime-обработчика, —
   * поэтому на один ответ уходило два POST. Ключ по якорю склеивает их все.
   */
  const advanceRef = useRef<{ anchorId: number | null; request: Promise<TestItem | null> } | null>(null)
  /** Идёт отправка ответа: realtime в это время очередь не двигает. */
  const answerInFlightRef = useRef(false)
  const syncStudentActiveItemRef = useRef<(r: Test | null | undefined) => Promise<void>>(async () => {})
  const [evaluatingAnswerIds, setEvaluatingAnswerIds] = useState<Record<number, true>>({})

  useEffect(() => {
    studentFocusRef.current = null
  }, [id])

  /**
   * Правки подсказок, ещё не гарантированно отражённые в ответе GET /tests/{id},
   * и поколения самих запросов прогона.
   *
   * `reload()` перетирает `run` целиком и вызывается отовсюду — из действий
   * учителя, из realtime на каждое событие и из фолбэк-опроса раз в 4 секунды.
   * Ответ, снятый ДО удаления подсказки, возвращал строку на экран, и повторный
   * клик по корзине снова давал 404. Поэтому: ответ старее уже применённого в
   * состояние не пускаем, а правки, случившиеся пока запрос летел, накатываем
   * поверх ответа.
   */
  const pendingClueOpsRef = useRef<{ seq: number; op: ClueOp }[]>([])
  const clueOpSeqRef = useRef(0)
  const runFetchSeqRef = useRef(0)
  const runAppliedSeqRef = useRef(0)
  /** Последний применённый прогон: его и отдаём вызывающим вместо устаревшего ответа. */
  const appliedRunRef = useRef<Test | null>(null)

  const reload = useCallback(async () => {
    if (!Number.isFinite(id)) return
    setError(null)
    const fetchSeq = ++runFetchSeqRef.current
    const clueSeqAtStart = clueOpSeqRef.current
    const fetched = await testsApi.get(id)
    // Устаревший ответ в состояние не пускаем и наружу не отдаём: вызывающие
    // строят по результату `activeItem` и фокус учителя, и разъехавшиеся `run`
    // и `activeItem` хуже, чем одинаково свежие.
    if (fetchSeq < runAppliedSeqRef.current) return appliedRunRef.current ?? fetched
    runAppliedSeqRef.current = fetchSeq
    const pending = pendingClueOpsRef.current.filter((entry) => entry.seq > clueSeqAtStart)
    pendingClueOpsRef.current = pending
    const ops = pending.map((entry) => entry.op)
    const r =
      ops.length && fetched ? { ...fetched, items: applyClueOpsToItems(fetched.items, ops) } : fetched
    appliedRunRef.current = r ?? null
    setRun(r)
    setResults((r as unknown as { results?: unknown })?.results as typeof results ?? null)
    if (r?.participants?.length && selectedParticipantIdRef.current == null) {
      setSelectedParticipantId(r.participants[0].id)
    }
    return r
  }, [id])

  /**
   * Слияние активного пункта с учётом ещё не отражённых сервером правок подсказок.
   *
   * `sendItemEvent` и `getNextItem` отдают пункт напрямую, мимо `reload`: ответ,
   * снятый до удаления подсказки, возвращал её в список ученика, и повторный
   * клик по корзине снова давал 404.
   */
  const mergeActiveItem = useCallback(
    (prev: TestItem | null, next: TestItem | null): TestItem | null => {
      const merged = mergeTestItemPreserveCard(prev, next)
      const pending = pendingClueOpsRef.current
      if (!merged?.card || !pending.length) return merged
      const card = applyClueOpsToCard(merged.card, pending.map((entry) => entry.op))
      return card === merged.card ? merged : { ...merged, card }
    },
    [],
  )

  const syncStudentActiveItem = useCallback(
    async (r: Test | null | undefined) => {
      if (!Number.isFinite(id) || r == null || resolveIsTeacherViewInTest(r, authUserId)) return
      // Тест урока ещё закрыт — не дёргаем next-item: он вернёт 422, а fallback-опрос
      // раз в 4 секунды превратил бы это в поток ошибок.
      if (r.can_interact === false) return
      if (runUsesTeacherDrivenItems(r)) {
        const { item, nextFocusId } = resolveStudentDisplayItemTeacherDriven(r, authUserId, studentFocusRef.current)
        studentFocusRef.current = nextFocusId
        setActiveItem((prev) => mergeActiveItem(prev, item))
        return
      }
      const list = studentScopedItems(r, authUserId)
      const fromRun = list.find((i) => i.status === 'active') ?? null
      if (fromRun) {
        studentFocusRef.current = fromRun.id
        setActiveItem((prev) => mergeActiveItem(prev, fromRun))
        return
      }

      const focusedId = studentFocusRef.current
      if (focusedId != null) {
        const focused = list.find((i) => i.id === focusedId) ?? null
        if (focused?.status === 'active') {
          setActiveItem((prev) => mergeActiveItem(prev, focused))
          return
        }
      }

      // Якорь — последняя карточка, которую видел ученик. По нему сервер
      // отличает повтор от честного продвижения, а мы склеиваем параллельные
      // вызовы из ответа и из realtime.
      const anchorId = focusedId ?? null

      const inFlight = advanceRef.current
      if (inFlight && inFlight.anchorId === anchorId) {
        const item = await inFlight.request
        if (item) {
          studentFocusRef.current = item.id
          setActiveItem((prev) => mergeActiveItem(prev, item))
        }
        return
      }

      const request = testsApi.getNextItem(id, anchorId)
      const entry = { anchorId, request }
      advanceRef.current = entry
      try {
        const item = await request
        if (item) {
          studentFocusRef.current = item.id
          setActiveItem((prev) => mergeActiveItem(prev, item))
        }
      } finally {
        if (advanceRef.current === entry) {
          advanceRef.current = null
        }
      }
    },
    [id, authUserId, mergeActiveItem],
  )

  useEffect(() => {
    syncStudentActiveItemRef.current = syncStudentActiveItem
  }, [syncStudentActiveItem])

  const handleRealtimeUpdate = useCallback(
    async (_type: 'started' | 'answer' | 'item' | 'completed' | 'cancelled') => {
      try {
        const r = await reload()
        if (r?.status === 'completed' || r?.status === 'cancelled') return
        // Пока ученик отправляет ответ, очередь двигает сам обработчик отправки.
        // Дело realtime — обновить состояние, а не форсировать следующую карточку:
        // иначе на один ответ уходило два продвижения.
        if (answerInFlightRef.current) return
        if (!resolveIsTeacherViewInTest(r ?? null, authUserId)) {
          await syncStudentActiveItemRef.current(r)
        }
      } catch {
        // ignore realtime errors (UI stays usable with manual reload)
      }
    },
    [reload, authUserId],
  )

  /**
   * Подсказки карточки изменились — правим прогон на месте, без перезагрузки.
   *
   * Панель рисует список из `item.card.clues`, а прогон обновляется только по
   * действиям и realtime: добавленная подсказка не появлялась в списке вовсе, а
   * удалённая продолжала висеть, и повторный клик по корзине давал 404.
   *
   * Правку запоминаем в `pendingClueOpsRef`: `reload()` перетирает `run`
   * целиком, а его ответ мог быть снят ДО правки — удалённая подсказка
   * возвращалась на экран, и 404 приходил снова. Ответ на запрос, начатый
   * раньше правки, догоняем теми же операциями (см. `reload`).
   */
  const applyClueOp = useCallback((op: ClueOp) => {
    clueOpSeqRef.current += 1
    pendingClueOpsRef.current = [...pendingClueOpsRef.current, { seq: clueOpSeqRef.current, op }]
    setRun((prev) => {
      if (!prev?.items?.length) return prev
      const items = applyClueOpsToItems(prev.items, [op])
      return items === prev.items ? prev : { ...prev, items }
    })
    setActiveItem((prev) => {
      if (!prev?.card) return prev
      const card = applyClueOpsToCard(prev.card, [op])
      return card === prev.card ? prev : { ...prev, card }
    })
  }, [])

  const handleCardClueAdded = useCallback(
    (cardId: number, clue: Clue) => applyClueOp({ cardId, add: clue }),
    [applyClueOp],
  )

  const handleCardClueDeleted = useCallback(
    (cardId: number, clueId: number) => applyClueOp({ cardId, removeId: clueId }),
    [applyClueOp],
  )

  /**
   * Подсказки карточки поменяла другая сторона — или сам пользователь в другой
   * вкладке (realtime `test.card.clues.changed`, текста в нагрузке нет).
   *
   * Перечитываем прогон: учитель видит подсказки из `run`, а ученику
   * обновляем только список подсказок показанной карточки. Очередь не
   * трогаем — `syncStudentActiveItem` в самостоятельном тесте мог бы запросить
   * следующую карточку из-за одной лишь правки подсказки. Своя правка уже
   * применена через `applyClueOp`, и её эхо ничего не дублирует: подсказка
   * добавляется по id один раз. Ученик группового теста не перечитывает прогон
   * из-за подсказок чужих карточек.
   */
  const handleCardCluesChanged = useCallback(
    async ({ card_id }: { card_id: number }) => {
      const known = appliedRunRef.current
      if (known) {
        const visible = runItemsVisibleToViewer(known, authUserId)
        if (visible.length > 0 && !visible.some((i) => i.card_id === card_id)) return
      }
      try {
        const r = await reload()
        setActiveItem((prev) => {
          if (!prev?.card || prev.card.id !== card_id) return prev
          const fresh = r?.items?.find((i) => i.id === prev.id)
          if (!fresh?.card) return prev
          const merged = mergeActiveItem(prev, { ...prev, card: { ...prev.card, clues: fresh.card.clues ?? [] } })
          if (!merged || sameClueIds(merged.card?.clues, prev.card.clues)) return prev
          return merged
        })
      } catch {
        // ignore realtime errors (UI stays usable with manual reload)
      }
    },
    [reload, authUserId, mergeActiveItem],
  )

  const applyTeacherEvaluationFromSocket = useCallback(
    (payload: { item_id: number; answer_id: number; is_correct: boolean }) => {
      setRun((prev) => {
        if (!prev?.items?.length) return prev
        const nextItems = prev.items.map((it) => {
          if (it.id !== payload.item_id) return it
          const answers = it.answers ?? []
          const nextAnswers = answers.map((a) =>
            a.id === payload.answer_id
              ? {
                  ...a,
                  is_correct: payload.is_correct,
                  checked_by: 'teacher' as const,
                  checked_at: new Date().toISOString(),
                }
              : a,
          )
          return { ...it, answers: nextAnswers }
        })
        return { ...prev, items: nextItems }
      })
      setActiveItem((prev) => {
        if (!prev || prev.id !== payload.item_id) return prev
        const answers = prev.answers ?? []
        const nextAnswers = answers.map((a) =>
          a.id === payload.answer_id
            ? {
                ...a,
                is_correct: payload.is_correct,
                checked_by: 'teacher' as const,
                checked_at: new Date().toISOString(),
              }
            : a,
        )
        return { ...prev, answers: nextAnswers }
      })
    },
    [],
  )

  const { presentUsers } = useTestRealtime(Number.isFinite(id) ? id : null, handleRealtimeUpdate, {
    authUserId,
    onTeacherEvaluationChanged: applyTeacherEvaluationFromSocket,
    onCardCluesChanged: (payload) => void handleCardCluesChanged(payload),
    onAnswerDraft: ({ item_id, user_id, text_preview }) => {
      setStudentDraftByItemId((prev) => ({
        ...prev,
        [item_id]: { user_id, text_preview },
      }))
    },
    onTeacherItemChanged: ({ participant_id }) => {
      void (async () => {
        try {
          const r = await reload()
          if (!r || resolveIsTeacherViewInTest(r, authUserId)) return
          if (itemsHaveParticipantScope(r)) {
            const myParticipant = (r.participants ?? []).find((p) => (p.user?.id ?? p.user_id) === authUserId)
            if (!myParticipant || myParticipant.id !== participant_id) return
          }
          await syncStudentActiveItemRef.current(r)
        } catch {
          // ignore
        }
      })()
    },
  })

  useEffect(() => {
    if (!Number.isFinite(id)) return
    let isMounted = true
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const r = await reload()
        if (!isMounted) return
        if (r?.status === 'completed') return
        if (!resolveIsTeacherViewInTest(r ?? null, authUserId)) {
          await syncStudentActiveItemRef.current(r)
        }
      } catch (e) {
        if (!isMounted) return
        setError(getErrorMessage(e))
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    })()
    return () => {
      isMounted = false
    }
  }, [id, reload, authUserId])

  const onlineUserIds = useMemo(() => new Set(presentUsers.map((u) => u.id)), [presentUsers])

  const selectedParticipant = useMemo(() => {
    if (!run?.participants?.length) return null
    return run.participants.find((p) => p.id === selectedParticipantId) ?? run.participants[0]
  }, [run, selectedParticipantId])

  const selectedParticipantItems = useMemo(() => {
    if (!selectedParticipant) return []
    return itemsScopedForParticipant(run, selectedParticipant.id)
  }, [run, selectedParticipant])

  const participantForResultsGauge = useMemo(() => {
    if (!run) return null
    return pickParticipantForResultsGauge(run, {
      isGroup,
      selectedParticipant,
      authUserId: authUser?.id ?? null,
      isTeacherLike,
    })
  }, [run, isGroup, selectedParticipant, authUser?.id, isTeacherLike])

  /** Тест из урока + устной формат ответа: ученик не шлёт текст в API. */
  const studentOralLessonCard = useMemo(
    () =>
      Boolean(
        run?.lesson_id != null && activeItem != null && isSpeechAnswerType(activeItem.answer_type ?? ''),
      ),
    [run?.lesson_id, activeItem],
  )

  const studentTeacherWaitingCopy = useMemo(() => {
    if (run && run.status === 'draft' && runUsesTeacherDrivenItems(run)) {
      return t('tests.student.waitingTeacherStart')
    }
    return t('tests.student.waiting')
  }, [run, t])

  const [nowTick, setNowTick] = useState(() => Date.now())
  useEffect(() => {
    if (!run || run.status === 'completed') return
    if (!run.started_at) return
    const timer = window.setInterval(() => setNowTick(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [run?.id, run?.status, run?.started_at, run])

  const testElapsedMs = useMemo(() => {
    if (!run?.started_at) return null
    const start = new Date(run.started_at).getTime()
    if (!Number.isFinite(start)) return null
    if (run.ended_at) {
      const end = new Date(run.ended_at).getTime()
      if (Number.isFinite(end)) return Math.max(0, end - start)
    }
    return Math.max(0, nowTick - start)
  }, [run, nowTick])

  /** Учитель + тест из урока: полный макет (DashboardLayout, очередь, участники) — в том числе для цели group (`target_type`). */
  const teacherLessonLikeLayout = Boolean(run && isTeacherLike && run.lesson_id != null)

  /**
   * Урок под учителем: карточка на экране до нажатия «Следующая», даже если она
   * уже completed, — и после перезагрузки тоже (см. `resolveTeacherDisplayItem`).
   */
  const teacherDisplayItem = useMemo(() => {
    if (run?.status === 'draft') return null
    return resolveTeacherDisplayItem(selectedParticipantItems)
  }, [run?.status, selectedParticipantItems])

  const teacherHasMoreCards = useMemo(() => {
    if (!teacherDisplayItem) return false
    const byOrder = [...selectedParticipantItems].sort((a, b) => a.order - b.order)
    const idx = byOrder.findIndex((i) => i.id === teacherDisplayItem.id)
    if (idx < 0) return false
    return byOrder.slice(idx + 1).some((i) => i.status === 'pending' || i.status === 'active')
  }, [selectedParticipantItems, teacherDisplayItem])

  /**
   * Отпечаток ответов активной карточки.
   *
   * По нему сбрасывается состояние ввода: «ответ появился» и «ответ получил
   * вердикт» — это разные карточки для ученика, хотя `id` позиции тот же.
   */
  const activeItemAnswersSignature = useMemo(
    () =>
      (activeItem?.answers ?? [])
        .map((a) => `${a.id}:${a.is_correct === null ? 'x' : String(a.is_correct)}:${a.unverifiable_at ? 'u' : ''}`)
        .join('|'),
    [activeItem?.answers],
  )

  /**
   * Ученик ответил на текущую карточку.
   *
   * Считается по данным, а не по флагу: в прогоне, который ведёт учитель, `id`
   * позиции после ответа не меняется, ресет-эффект срабатывал на изменившемся
   * составе ответов и снимал блокировку — поле ввода возвращалось, и на ту же
   * карточку можно было ответить второй раз. Предикат тот же, что на сервере:
   * строка ответа сама по себе ничего не значит, значим payload или вердикт.
   */
  const studentHasAnswerOnServer = useMemo(
    () =>
      (activeItem?.answers ?? []).some(
        (a) => a.answer_payload != null || a.is_correct != null || a.unverifiable_at != null,
      ),
    [activeItem?.answers],
  )

  const studentAnswered = studentAnsweredLocal || studentHasAnswerOnServer

  useEffect(() => {
    // reset per-card student UI state when card changes
    // подсказки/подсказки других учеников — открываются один раз и не скрываются (persist)
    const itemId = activeItem?.id
    const openedCluesKey =
      Number.isFinite(run?.id) && Number.isFinite(itemId)
        ? `testrun:${run?.id}:item:${itemId}:clues_opened`
        : null
    const openedClues =
      (openedCluesKey ? window.localStorage.getItem(openedCluesKey) === '1' : false) || Boolean(activeItem?.hint_used)

    const openedTranscriptionKey =
      Number.isFinite(run?.id) && Number.isFinite(itemId)
        ? `testrun:${run?.id}:item:${itemId}:transcription_opened`
        : null
    const openedTranscription =
      (openedTranscriptionKey ? window.localStorage.getItem(openedTranscriptionKey) === '1' : false) ||
      Boolean(activeItem?.transcription_used)

    setStudentTranscriptionOpen(openedTranscription)
    setStudentCluesOpen(openedClues)
    setStudentSelectedChoice(null)
    setStudentAnsweredLocal(false)
    setAnswerText('')
    // Сбрасываем не только по смене карточки: зависшая позиция сохраняла тот же
    // `id`, и поле ввода с кнопкой «На проверке» оставались заблокированными
    // навсегда. Статус и состав ответов — тот же сигнал «карточка стала другой».
  }, [
    activeItem?.id,
    activeItem?.status,
    activeItemAnswersSignature,
    activeItem?.hint_used,
    activeItem?.transcription_used,
    run?.id,
  ])

  const handleStudentShowClues = useCallback(() => {
    const itemId = activeItem?.id
    if (Number.isFinite(run?.id) && Number.isFinite(itemId)) {
      const key = `testrun:${run?.id}:item:${itemId}:clues_opened`
      window.localStorage.setItem(key, '1')
    }
    setStudentCluesOpen(true)
    // "показать подсказки" также фиксирует hint_used (односторонне)
    if (run && activeItem?.allow_hints && !activeItem.hint_used) {
      void (async () => {
        try {
          const updated = await testsApi.sendItemEvent(run.id, activeItem.id, 'hint_opened')
          setActiveItem((prev) => mergeActiveItem(prev, updated))
          const r2 = await reload()
          await syncStudentActiveItemRef.current(r2)
        } catch {
          // ignore
        }
      })()
    }
  }, [run, activeItem, reload, mergeActiveItem])

  useEffect(() => {
    if (autoCheckFeedback == null) return
    const timer = window.setTimeout(() => setAutoCheckFeedback(null), 2800)
    return () => window.clearTimeout(timer)
  }, [autoCheckFeedback])

  const handleSubmitAnswer = async () => {
    if (!run || !activeItem) return
    if (isSpeechAnswerType(activeItem.answer_type ?? '')) return
    setSubmitting(true)
    setError(null)
    setAutoCheckFeedback(null)
    try {
      const skill = normalizeSkillType(String(activeItem.skill_type ?? 'translation'))
      let answerType = activeItem.answer_type?.trim()
      if (!answerType) {
        const opts = allowedAnswerTypesForSkill(activeItem.prompt_type, skill)
        answerType = opts[0] ?? 'typed'
      }
      const mode = answerTypeFormatMode(answerType)
      const textPayload = mode === 'choose' ? (studentSelectedChoice ?? '').trim() : answerText.trim()
      if (!textPayload) {
        setSubmitting(false)
        return
      }
      answerInFlightRef.current = true
      // Черновик по этой карточке больше не нужен: она сейчас закроется, и
      // отложенное событие уехало бы на уже неактивную позицию (422).
      draftTimerRef.current.cancel()

      const { answers: submittedAnswers } = await testsApi.submitAnswer(run.id, activeItem.id, {
        skill_type: skill,
        answer_type: answerType,
        answer_payload: { text: textPayload },
      })
      const rAfter = await reload()
      // Якорь остаётся на отвеченной карточке: сервер по нему поймёт, что мы
      // двигаемся именно от неё, и повтор не съест следующее слово.
      await syncStudentActiveItemRef.current(rAfter)
      const allGraded =
        submittedAnswers.length > 0 &&
        submittedAnswers.every((a) => a.is_correct === true || a.is_correct === false)
      if (allGraded) {
        const itemId = activeItem.id
        if (submittedAnswers.every((a) => a.is_correct === true)) {
          setAutoCheckFeedback({ itemId, verdict: 'correct' })
        } else if (submittedAnswers.some((a) => a.is_correct === false)) {
          setAutoCheckFeedback({ itemId, verdict: 'incorrect' })
        }
      }
      setStudentAnsweredLocal(true)
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      answerInFlightRef.current = false
      setSubmitting(false)
    }
  }

  /**
   * Черновик ответа: отправляем только по живой карточке.
   *
   * Таймер на три секунды переживал и закрытие позиции, и отправку ответа —
   * событие уходило на уже неактивную карточку и возвращалось 422 при обычном
   * прохождении теста.
   *
   * Зависимости — только примитивы: `run` и `activeItem` пересоздаёт каждое
   * перечитывание прогона (realtime, в том числе правки подсказок), и таймер
   * перезапускался без единого нажатия — через три секунды уходил тот же
   * черновик. Пустой текст не шлём: API превращает его в null и отвечает 422.
   */
  const draftRunId = run?.id ?? null
  const draftItemId = activeItem?.id ?? null
  const draftItemStatus = activeItem?.status ?? null
  const draftAnswerType = activeItem?.answer_type ?? null
  useEffect(() => {
    const draftOnStart = draftTimerRef.current
    draftOnStart.cancel()
    if (draftRunId == null || draftItemId == null) return
    if (isTeacherLike) return
    if (studentAnswered) return
    if (draftItemStatus !== 'active') return
    if (isSpeechAnswerType(draftAnswerType ?? '')) return
    const mode = answerTypeFormatMode(draftAnswerType?.trim() ?? '')
    if (mode !== 'typed') return
    const text = answerText
    if (text.trim() === '') return
    const draft = draftTimerRef.current
    const timer = window.setTimeout(() => {
      // Черновик — подсказка учителю, не данные: его отказ странице не важен.
      testsApi.sendItemEvent(draftRunId, draftItemId, 'answer_draft', { text }).catch(() => {})
    }, 3000)
    draft.set(timer)
    return () => draft.cancel()
  }, [draftRunId, draftItemId, draftItemStatus, draftAnswerType, answerText, isTeacherLike, studentAnswered])

  const handleStudentShowTranscription = useCallback(async () => {
    if (!activeItem) return
    setStudentTranscriptionOpen(true)

    const itemId = activeItem.id
    if (Number.isFinite(run?.id) && Number.isFinite(itemId)) {
      const key = `testrun:${run?.id}:item:${itemId}:transcription_opened`
      window.localStorage.setItem(key, '1')
    }

    if (!run || activeItem.transcription_used) return
    try {
      const updated = await testsApi.sendItemEvent(run.id, activeItem.id, 'transcription_opened')
      setActiveItem((prev) => mergeActiveItem(prev, updated))
      const r2 = await reload()
      await syncStudentActiveItemRef.current(r2)
    } catch {
      // ignore events failure (UX still ok)
    }
  }, [run, activeItem, reload, mergeActiveItem])

  const handleStudentAudioPlayed = useCallback(async () => {
    if (!run || !activeItem) return
    try {
      const updated = await testsApi.sendItemEvent(run.id, activeItem.id, 'audio_played')
      setActiveItem((prev) => mergeActiveItem(prev, updated))
      const r2 = await reload()
      await syncStudentActiveItemRef.current(r2)
    } catch {
      // ignore
    }
  }, [run, activeItem, reload, mergeActiveItem])

  const studentAllItems = useMemo(() => {
    return studentScopedItems(run, authUserId)
  }, [run, authUserId])

  const studentProgress = useMemo(() => {
    const total = studentAllItems.length
    if (!activeItem || total === 0) return { currentIndex: 0, total }
    const idx = studentAllItems.findIndex((x) => x.id === activeItem.id)
    return { currentIndex: idx >= 0 ? idx + 1 : Math.min(total, Math.max(1, activeItem.order)), total }
  }, [studentAllItems, activeItem])

  const studentTimerText = useMemo(() => {
    if (testElapsedMs == null) return null
    const durationSec = run?.duration_sec ?? null
    if (durationSec != null && durationSec > 0) {
      const left = Math.max(0, durationSec * 1000 - testElapsedMs)
      return formatDurationMs(left)
    }
    return formatDurationMs(testElapsedMs)
  }, [run?.duration_sec, testElapsedMs])

  const pendingEvalOnCurrentItem = useMemo(
    () => answersPendingTeacherEvaluation(teacherDisplayItem?.answers),
    [teacherDisplayItem],
  )

  const activeItemAutoCheck = useMemo(() => {
    if (!run || !teacherDisplayItem) return false
    return itemAutoCheckApplies(run, teacherDisplayItem)
  }, [run, teacherDisplayItem])

  const activeItemIsOral = useMemo(
    () =>
      Boolean(
        teacherDisplayItem &&
          isSpeechAnswerType(teacherDisplayItem.answer_type ?? ''),
      ),
    [teacherDisplayItem],
  )

  const advanceNextCardDisabled = useMemo(
    () =>
      pendingEvalOnCurrentItem.length > 0,
    [pendingEvalOnCurrentItem.length],
  )

  const teacherAdvanceNextTooltip = useMemo(() => {
    if (pendingEvalOnCurrentItem.length > 0) return t('tests.teacher.individual.nextCardBlockedPendingEval')
    return t('tests.teacher.individual.nextCardHelp')
  }, [pendingEvalOnCurrentItem.length, t])

  const handleTeacherEvaluate = async (itemId: number, answerId: number, isCorrect: boolean) => {
    if (!run) return
    setEvaluatingAnswerIds((prev) => ({ ...prev, [answerId]: true }))
    setError(null)
    try {
      await testsApi.teacherEvaluate(run.id, itemId, { answer_id: answerId, is_correct: isCorrect })
      await reload()
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setEvaluatingAnswerIds((prev) => {
        const next = { ...prev }
        delete next[answerId]
        return next
      })
    }
  }

  const handleTeacherAdvanceNextCard = async () => {
    if (!run || isCompleted) return
    if (itemsHaveParticipantScope(run) && !selectedParticipant) return
    setSubmitting(true)
    setError(null)
    try {
      if (itemsHaveParticipantScope(run)) {
        await testsApi.teacherAdvanceParticipant(run.id, selectedParticipant!.id)
      } else {
        await testsApi.getNextItem(run.id)
      }
      // Открытая карточка стала active — её и покажет teacherDisplayItem.
      await reload()
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  const handleTeacherCompleteRun = async () => {
    if (!run || isCompleted) return
    const incompleteScopeId = itemsHaveParticipantScope(run) ? selectedParticipant?.id ?? null : null
    if (
      runHasIncompleteItems(run, incompleteScopeId) &&
      !window.confirm(
        t('tests.teacher.individual.completeConfirm', {
          defaultValue: 'Завершить тест сейчас? Незавершённые карточки будут отмечены как пропущенные.',
        }),
      )
    ) {
      return
    }
    setCompletingRun(true)
    setError(null)
    try {
      await testsApi.complete(run.id)
      await reload()
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setCompletingRun(false)
    }
  }

  /**
   * Завершение самостоятельного прогона самим учеником.
   *
   * Основной путь — автозавершение при исчерпанной очереди; эта кнопка нужна,
   * чтобы прервать прогон на середине, иначе брошенный самотест висел бы в
   * списке тестов навсегда.
   */
  const handleStudentCompleteRun = async () => {
    if (!run || isCompleted) return
    if (
      runHasIncompleteItems(run, null) &&
      !window.confirm(t('tests.run.completeConfirm'))
    ) {
      return
    }
    setCompletingRun(true)
    setError(null)
    try {
      await testsApi.complete(run.id)
      await reload()
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setCompletingRun(false)
    }
  }

  /** Прервать можно только свой самостоятельный прогон: урочный закрывает учитель. */
  const canStudentCompleteRun = Boolean(
    run &&
      !isTeacherLike &&
      run.lesson_id == null &&
      run.status !== 'completed' &&
      run.status !== 'cancelled',
  )

  if (loading && isTeacherLike) {
    return <TestPageTeacherLoadingSkeleton runId={Number.isFinite(id) ? id : 0} t={t} />
  }

  if (teacherLessonLikeLayout && run) {
    return (
      <DashboardLayout>
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            pb: 3,
            maxWidth: 1200,
            mx: 'auto',
            width: '100%',
            minHeight: 'calc(100vh - 120px)',
          }}
        >
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}

          {closedWithLesson ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(`tests.run.closedWithLesson.${closedWithLesson}`)}
            </Alert>
          ) : null}

          {notStartedNotice ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {notStartedNotice}
            </Alert>
          ) : null}

          <TestPageBreadcrumbs t={t} run={run} runId={run.id} isTeacherLike />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Typography variant="h4" component="h1" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
              {t('tests.run.title', { id: run.id })}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                width: { xs: '100%', lg: 'auto' },
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {isCompleted ? (
                <Stack spacing={2}>
                  {results ? (
                    <TestResultsSummaryWithGauge
                      results={results}
                      participant={participantForResultsGauge}
                      t={t}
                      wrap="card"
                    />
                  ) : (
                    <CircularProgress size={20} />
                  )}
                  <TestCompletedCardsList items={run.items ?? []} t={t} />
                </Stack>
              ) : (
                <>
                  <TeacherLessonActiveCardFocus
                    item={teacherDisplayItem}
                    t={t}
                    evaluatingAnswerIds={evaluatingAnswerIds}
                    advanceNextSubmitting={submitting}
                    onTeacherEvaluate={(itemId, answerId, ok) => void handleTeacherEvaluate(itemId, answerId, ok)}
                    advanceNextDisabled={advanceNextCardDisabled}
                    advanceNextTooltip={teacherAdvanceNextTooltip}
                    onAdvanceNextCard={() => void handleTeacherAdvanceNextCard()}
                    testCompleted={isCompleted}
                    autoCheckOnThisItem={activeItemAutoCheck}
                    itemIsOral={activeItemIsOral}
                    hasMoreCards={teacherHasMoreCards}
                    waitingForTeacherStart={run.status === 'draft'}
                    onCardClueAdded={handleCardClueAdded}
                    onCardClueDeleted={handleCardClueDeleted}
                    studentAnswerDraftPreview={
                      teacherDisplayItem?.id
                        ? (studentDraftByItemId[teacherDisplayItem.id]?.text_preview ?? null)
                        : null
                    }
                  />

                  {selectedParticipantItems.length > 0 ? (
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ py: 0.5, px: 0.75, '&:last-child': { pb: 0.75 } }}>
                        <Stack divider={<Divider sx={{ opacity: 0.4 }} />} sx={{ py: 0.25 }}>
                          {selectedParticipantItems.map((it) => {
                            const isCurrent = teacherDisplayItem?.id === it.id
                            const word =
                              (it.card?.word?.text && it.card.word.text.trim()) ||
                              t('tests.run.fallbackCard', { id: it.card_id })
                            const skills = itemEvaluatedSkillList(it)

                            const leftBarColor = (theme: Theme) => {
                              return studentItemIndicatorColor(it, isCurrent, theme)
                            }
                            return (
                              <Box
                                key={it.id}
                                sx={(theme) => ({
                                  display: 'grid',
                                  gridTemplateColumns: '6px 1fr',
                                  gap: 1,
                                  alignItems: 'stretch',
                                  px: 1,
                                  py: 0.75,
                                  bgcolor:
                                    isCurrent
                                      ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.06 : 0.12)
                                      : 'transparent',
                                })}
                              >
                                <Box
                                  sx={(theme) => ({
                                    width: 2,
                                    height: '100%',
                                    borderRadius: 999,
                                    bgcolor: leftBarColor(theme),
                                    justifySelf: 'center',
                                    alignSelf: 'stretch',
                                  })}
                                >
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        flex: 1,
                                        fontWeight: isCurrent ? 700 : 500,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={word}
                                    >
                                      {word}
                                    </Typography>

                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      flexWrap="wrap"
                                      justifyContent="flex-end"
                                      alignItems="center"
                                      sx={{ minWidth: 0, ml: 'auto' }}
                                    >
                                      {/* Mobile: компактные чипы целей (как было) */}
                                      <Box sx={{ display: { xs: 'flex', lg: 'none' }, gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                        {skills.map((sk, idx) => {
                                          const ans = findAnswerForEvaluatedSkill(it, sk)
                                          return (
                                            <Chip
                                              key={`${sk}-${idx}`}
                                              size="small"
                                              label={skillChipShortLabel(sk, t)}
                                              variant="outlined"
                                              sx={(theme) => ({
                                                height: 22,
                                                '& .MuiChip-label': { px: 0.75, fontSize: '0.6rem' },
                                                ...chipSxForTeacherEvalSkill(it, sk, ans, theme),
                                              })}
                                            />
                                          )
                                        })}
                                      </Box>

                                      {/* Desktop: все текстовые бейджи (prompt + skills + answerType + auto) */}
                                      <Box
                                        sx={{
                                          display: { xs: 'none', lg: 'flex' },
                                          gap: 0.5,
                                          flexWrap: 'wrap',
                                          justifyContent: 'flex-end',
                                          alignItems: 'center',
                                        }}
                                      >
                                        {(() => {
                                          const iconSx = { fontSize: 16 }
                                          const chipSx = {
                                            height: 24,
                                            maxWidth: 148,
                                            '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis' },
                                            '& .MuiChip-icon': { ml: 0.5 },
                                          } as const

                                          const showAuto = answerTypeSupportsAutoChecking(it.answer_type)

                                          return (
                                            <>
                                              <Tooltip
                                                title={t('tests.run.badges.tooltipDisplay', {
                                                  value: promptTypeHuman(it.prompt_type, t),
                                                })}
                                              >
                                                <Chip
                                                  size="small"
                                                  variant="outlined"
                                                  icon={<VisibilityOutlined sx={iconSx} />}
                                                  label={promptTypeHuman(it.prompt_type, t)}
                                                  sx={chipSx}
                                                />
                                              </Tooltip>

                                              {skills.length === 0 ? (
                                                <Tooltip title={t('tests.run.badges.tooltipSkill', { value: '—' })}>
                                                  <Chip size="small" variant="outlined" icon={<Adjust sx={iconSx} />} label="—" sx={chipSx} />
                                                </Tooltip>
                                              ) : (
                                                skills.map((sk, idx) => {
                                                  const ans = findAnswerForEvaluatedSkill(it, sk)
                                                  return (
                                                    <Tooltip
                                                      key={`${sk}-${idx}`}
                                                      title={t('tests.run.badges.tooltipSkill', { value: skillCheckLabel(sk, t) })}
                                                    >
                                                      <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        icon={<Adjust sx={iconSx} />}
                                                        label={skillCheckLabel(sk, t)}
                                                        sx={(theme) => ({
                                                          ...chipSx,
                                                          ...chipSxForTeacherEvalSkill(it, sk, ans, theme),
                                                        })}
                                                      />
                                                    </Tooltip>
                                                  )
                                                })
                                              )}

                                              {it.answer_type ? (
                                                <Tooltip
                                                  title={t('tests.run.badges.tooltipAnswer', {
                                                    value: answerTypeShortLabel(it.answer_type, t),
                                                  })}
                                                >
                                                  <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    icon={<ChatBubbleOutline sx={iconSx} />}
                                                    label={answerTypeShortLabel(it.answer_type, t)}
                                                    sx={chipSx}
                                                  />
                                                </Tooltip>
                                              ) : null}

                                              {showAuto ? (
                                                <Tooltip title={t('tests.run.badges.autoTooltip')}>
                                                  <Chip
                                                    size="small"
                                                    label={t('tests.run.badges.auto')}
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{ height: 24, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                                                  />
                                                </Tooltip>
                                              ) : null}
                                            </>
                                          )
                                        })()}
                                      </Box>
                                    </Stack>
                                  </Stack>
                                </Box>
                              </Box>
                            )
                          })}
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : null}
                </>
              )}
            </Box>

            <Box
              sx={{
                width: { xs: '100%', lg: 360 },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Card sx={{ borderRadius: 2 }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {t('tests.teacher.individual.testInfo')}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'baseline' }}>
                    <Typography color="text.secondary" variant="body2">
                      {t('tests.run.number')}
                    </Typography>
                    <Typography fontWeight={700} variant="body2">
                      #{run.id}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'baseline' }}>
                    <Typography color="text.secondary" variant="body2">
                      {t('tests.run.startedAt')}
                    </Typography>
                    <Typography fontWeight={700} variant="body2" textAlign="right">
                      {formatRunDate(run.started_at, i18n.language)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'baseline' }}>
                    <Typography color="text.secondary" variant="body2">
                      {t('tests.teacher.individual.duration')}
                    </Typography>
                    <Typography fontWeight={700} variant="body2">
                      {testElapsedMs != null ? formatDurationMs(testElapsedMs) : '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'baseline' }}>
                    <Typography color="text.secondary" variant="body2">
                      {t('tests.run.endedAt')}
                    </Typography>
                    <Typography fontWeight={700} variant="body2" textAlign="right">
                      {formatRunDate(run.ended_at, i18n.language)}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={t(`tests.runStatus.${run.status}`, { defaultValue: run.status })}
                    color={
                      run.status === 'active'
                        ? 'primary'
                        : run.status === 'completed'
                          ? 'success'
                          : run.status === 'cancelled'
                            ? 'error'
                            : run.status === 'expired'
                              ? 'warning'
                              : run.status === 'draft'
                                ? 'warning'
                                : 'default'
                    }
                    variant={run.status === 'draft' ? 'outlined' : 'filled'}
                    sx={{ alignSelf: 'flex-start' }}
                  />
                  {run.lesson_id != null ? (
                    <Button
                      component={RouterLink}
                      to={`/lessons/${run.lesson_id}`}
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                    >
                      {t('tests.run.lessonLink', { id: run.lesson_id })}
                    </Button>
                  ) : null}

                  {run.status === 'draft' ? (
                    <>
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none' }}
                        disabled={
                          submitting ||
                          run.can_interact === false ||
                          (itemsHaveParticipantScope(run) && !selectedParticipant)
                        }
                        onClick={() => void handleTeacherAdvanceNextCard()}
                      >
                        {submitting
                          ? t('common.saving', { defaultValue: 'Сохранение...' })
                          : t('tests.teacher.individual.startTest')}
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                        {notStartedNotice ?? t('tests.teacher.individual.startTestHelp')}
                      </Typography>
                    </>
                  ) : null}

                  {!isCompleted ? (
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      color="error"
                      sx={{ textTransform: 'none' }}
                      disabled={completingRun || run.status === 'draft'}
                      onClick={() => void handleTeacherCompleteRun()}
                    >
                      {completingRun
                        ? t('common.saving', { defaultValue: 'Сохранение...' })
                        : t('tests.teacher.individual.completeNow', { defaultValue: 'Завершить тест' })}
                    </Button>
                  ) : null}
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                    {t('tests.teacher.individual.participants')}
                  </Typography>
                  {(run.participants ?? []).length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      —
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {(run.participants ?? []).map((p) => {
                        const isOnline = onlineUserIds.has(p.user?.id ?? p.user_id)
                        const progress = computeParticipantProgress(run, p.id)
                        const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0
                        const selected = p.id === selectedParticipantId
                        const name = pickParticipantDisplayName(p)
                        return (
                          <ListItemButton
                            key={p.id}
                            selected={selected}
                            onClick={() => setSelectedParticipantId(p.id)}
                            sx={{
                              borderRadius: 2,
                              mb: 0.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              '&.Mui-selected': {
                                borderColor: 'primary.main',
                                bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.06 : 0.12),
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                              <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                                <Avatar
                                  src={p.user?.avatar_url ?? undefined}
                                  sx={(theme) => ({
                                    width: 32,
                                    height: 32,
                                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.22),
                                    color: 'primary.main',
                                    fontWeight: 700,
                                  })}
                                >
                                  {(name || '#').slice(0, 1).toUpperCase()}
                                </Avatar>
                                <Box
                                  component="span"
                                  sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: isOnline ? 'success.main' : 'action.disabled',
                                    border: '2px solid',
                                    borderColor: 'background.paper',
                                    boxSizing: 'border-box',
                                  }}
                                />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={700} noWrap>
                                  {name}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={pct}
                                  sx={{ mt: 0.5, height: 6, borderRadius: 99 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {progress.done}/{progress.total}
                                </Typography>
                              </Box>
                            </Box>
                          </ListItemButton>
                        )
                      })}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
          maxWidth: 1200,
          width: '100%',
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          pt: 2,
          pb: 3,
        }}
      >
        <Box sx={{ flexShrink: 0, width: '100%' }}>
          <TestPageBreadcrumbs
            t={t}
            run={run}
            runId={Number.isFinite(id) ? id : 0}
            isTeacherLike={isTeacherLike}
            sx={{ mb: 2 }}
          />
          {closedWithLesson ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(`tests.run.closedWithLesson.${closedWithLesson}`)}
            </Alert>
          ) : null}
          {notStartedNotice ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {notStartedNotice}
            </Alert>
          ) : null}
        </Box>
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0, width: '100%' }}>
          {loading ? (
            <TestPageStudentLoadingSkeleton />
          ) : (
            <>
              {error ? (
                <Box sx={{ p: 2, width: '100%' }}>
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                </Box>
              ) : null}

              {run ? (
                <>
                  {isTeacherLike && isGroup ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      width: 320,
                      minWidth: 320,
                      maxWidth: 320,
                      borderRadius: 0,
                      borderLeft: 0,
                      borderTop: 0,
                      borderBottom: 0,
                      overflow: 'auto',
                    }}
                  >
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t('tests.teacher.group.participants')}
                      </Typography>
                      <List dense disablePadding>
                        {(run.participants ?? []).map((p) => {
                          const isOnline = onlineUserIds.has(p.user?.id ?? p.user_id)
                          const progress = computeParticipantProgress(run, p.id)
                          const pct =
                            progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0
                          const selected = p.id === selectedParticipantId
                          return (
                            <ListItemButton
                              key={p.id}
                              selected={selected}
                              onClick={() => setSelectedParticipantId(p.id)}
                              sx={{ borderRadius: 1, mb: 0.5 }}
                            >
                              <Box sx={{ mr: 1.25, display: 'flex', alignItems: 'center' }}>
                                <CircleIcon
                                  fontSize="small"
                                  sx={{
                                    color: isOnline ? 'success.main' : 'text.disabled',
                                    transition: 'color 300ms ease',
                                  }}
                                />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <ListItemText
                                  primary={pickParticipantDisplayName(p)}
                                  primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                                />
                                <Box sx={{ mt: 0.5 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={pct}
                                    sx={{ height: 6, borderRadius: 99 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {progress.done}/{progress.total}
                                  </Typography>
                                </Box>
                              </Box>
                            </ListItemButton>
                          )
                        })}
                      </List>
                    </Box>
                  </Paper>
                ) : null}

                <Box sx={{ flex: 1, minWidth: 0, p: 2, overflow: 'auto' }}>
                  {isCompleted ? (
                    <Stack spacing={2}>
                      {results ? (
                        <TestResultsSummaryWithGauge
                          results={results}
                          participant={participantForResultsGauge}
                          t={t}
                          wrap="paper"
                        />
                      ) : (
                        <CircularProgress size={20} />
                      )}
                      <TestCompletedCardsList items={run.items ?? []} t={t} />
                    </Stack>
                  ) : isTeacherLike && isGroup ? (
                      <Stack spacing={2}>
                        <Typography variant="h6">
                          {t('tests.teacher.group.student')}:{' '}
                          {selectedParticipant ? pickParticipantDisplayName(selectedParticipant) : '—'}
                        </Typography>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t('tests.teacher.group.currentCard')}
                          </Typography>
                          {teacherDisplayItem ? (
                            <Stack spacing={1}>
                              <TestCardFace
                                card={teacherDisplayItem.card}
                                cardId={teacherDisplayItem.card_id}
                                t={t}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {t('tests.item.status')}: {teacherDisplayItem.status} · {t('tests.item.promptType')}:{' '}
                                {teacherDisplayItem.prompt_type}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('tests.teacher.group.noActiveCard')}
                            </Typography>
                          )}
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t('tests.teacher.group.allCards')}
                          </Typography>
                          <List dense disablePadding>
                            {selectedParticipantItems.map((it) => (
                              <ListItemButton
                                key={it.id}
                                sx={{ alignItems: 'flex-start', borderRadius: 1, mb: 0.5 }}
                                disabled
                              >
                                <ListItemText
                                  primary={<TestCardFace card={it.card} cardId={it.card_id} t={t} />}
                                  primaryTypographyProps={{ component: 'div' }}
                                  secondary={`${t('tests.item.status')}: ${it.status}`}
                                  secondaryTypographyProps={{ sx: { mt: 0.75 } }}
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        </Paper>
                      </Stack>
                  ) : !isTeacherLike ? (
                    <Box
                      sx={{
                        maxWidth: { xs: '100%', sm: 520, lg: 1200 },
                        mx: 'auto',
                        display: { xs: 'flex', lg: 'grid' },
                        flexDirection: { xs: 'column', lg: 'unset' },
                        // left column ~33% narrower (was 420px)
                        gridTemplateColumns: { lg: '280px 1fr' },
                        gap: { xs: 2, lg: 3 },
                        alignItems: 'start',
                      }}
                    >
                      {/* Left column (scrollable list on large screens) */}
                      <Box
                        sx={{
                          order: { xs: 2, lg: 1 },
                          gridColumn: { lg: '1' },
                          gridRow: { lg: 2 },
                          width: { xs: '100%', lg: 'auto' },
                          maxHeight: { lg: 'calc(100vh - 160px)' },
                          overflowY: { lg: 'auto' },
                          pr: { lg: 1 },
                        }}
                      >
                        {/* All cards table */}
                        <Box sx={{ pt: 0.5 }}>
                          <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                            <Stack divider={<Divider sx={{ opacity: 0.4 }} />} sx={{ py: 0.25 }}>
                              {studentAllItems.map((it) => {
                                const isCurrent = activeItem?.id === it.id
                                const isReversePrompt = it.prompt_type === PROMPT_TRANSLATION_TEXT
                                const revealsAnswer =
                                  it.status === 'completed' || it.status === 'skipped'
                                // При обратном переводе оригинал — это ответ, поэтому до завершения
                                // элемента в списке показываем русский вариант (перевод) вместо него.
                                const word =
                                  isReversePrompt && !revealsAnswer
                                    ? cardWordTranslationOnly(it.card ?? undefined) ||
                                      t('tests.run.hiddenAnswerWord', {
                                        defaultValue: '••••',
                                        number: studentAllItems.indexOf(it) + 1,
                                      })
                                    : (it.card?.word?.text && it.card.word.text.trim()) ||
                                      t('tests.run.fallbackCard', { id: it.card_id })
                                const skills = itemEvaluatedSkillList(it)

                                const leftBarColor = (theme: Theme) => {
                                  return studentItemIndicatorColor(it, isCurrent, theme)
                                }

                                return (
                                  <Box
                                    key={it.id}
                                    sx={(theme) => ({
                                      display: 'grid',
                                      gridTemplateColumns: '6px 1fr',
                                      gap: 1,
                                      alignItems: 'stretch',
                                      px: 1.25,
                                      py: 1,
                                      bgcolor:
                                        isCurrent
                                          ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.06 : 0.12)
                                          : 'transparent',
                                    })}
                                  >
                                    <Box
                                      sx={(theme) => ({
                                        width: 2,
                                        height: '100%',
                                        borderRadius: 999,
                                        bgcolor: leftBarColor(theme),
                                        justifySelf: 'center',
                                        alignSelf: 'stretch',
                                      })}
                                    />

                                    <Box sx={{ minWidth: 0 }}>
                                      <Stack
                                          direction="row"
                                        spacing={{ xs: 1, lg: 0.75 }}
                                        alignItems={{ xs: 'center', lg: 'stretch' }}
                                        sx={{ minWidth: 0 }}
                                      >
                                        <Typography
                                          variant="body1"
                                          sx={{
                                            flex: 1,
                                            fontWeight: 500,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                          title={word}
                                        >
                                          {word}
                                        </Typography>
                                        {/* Mobile: chips, Desktop: dots aligned right */}
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          flexWrap="nowrap"
                                          justifyContent="flex-end"
                                          alignItems="center"
                                          sx={{ minWidth: 0, ml: 'auto' }}
                                        >
                                          <Box sx={{ display: { xs: 'flex', lg: 'none' }, gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            {skills.map((sk, idx) => {
                                              const ans = findAnswerForEvaluatedSkill(it, sk)
                                              return (
                                                <Chip
                                                  key={`${sk}-${idx}`}
                                                  size="small"
                                                  label={skillChipShortLabel(sk, t)}
                                                  variant="outlined"
                                                  sx={(theme) => ({
                                                    height: 22,
                                                    '& .MuiChip-label': { px: 0.75, fontSize: '0.6rem' },
                                                    ...chipSxForTeacherEvalSkill(it, sk, ans, theme),
                                                  })}
                                                />
                                              )
                                            })}
                                          </Box>

                                          <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 0.75, alignItems: 'center' }}>
                                            {skills.map((sk, idx) => {
                                              const ans = findAnswerForEvaluatedSkill(it, sk)
                                              return (
                                                <Tooltip key={`${sk}-${idx}`} title={t('tests.run.badges.tooltipSkill', { value: skillCheckLabel(sk, t) })}>
                                                  <Box
                                                    sx={(theme) => {
                                                      const c = skillDotColor(it, sk, ans, theme)
                                                      return {
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: '50%',
                                                        bgcolor: c,
                                                        boxShadow: `0 0 0 3px ${alpha(c, theme.palette.mode === 'light' ? 0.10 : 0.18)}`,
                                                        flexShrink: 0,
                                                      }
                                                    }}
                                                  />
                                                </Tooltip>
                                              )
                                            })}
                                          </Box>
                                        </Stack>
                                      </Stack>
                                    </Box>
                                  </Box>
                                )
                              })}
                            </Stack>
                          </Paper>
                        </Box>
                      </Box>

                      {/* Topbar (full width above columns) */}
                      <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{
                          gridColumn: { lg: '1 / -1' },
                          gridRow: { lg: 1 },
                          borderRadius: 0,
                          borderLeft: 0,
                          borderRight: 0,
                          borderTop: 0,
                          bgcolor: 'background.paper',
                          width: '100%',
                          mb: { xs: 2, lg: 2.5 },
                        }}
                      >
                        <Box sx={{ px: 1.5, py: 1.25 }}>
                          <Stack spacing={0.75}>
                            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: '100%' }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={
                                    studentProgress.total > 0
                                      ? Math.round((studentProgress.currentIndex / studentProgress.total) * 100)
                                      : 0
                                  }
                                  sx={{
                                    height: 8,
                                    borderRadius: 999,
                                    bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                    '& .MuiLinearProgress-bar': { borderRadius: 999 },
                                  }}
                                />
                              </Box>
                              {studentTimerText ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={studentTimerText}
                                  sx={(theme) => ({
                                    flexShrink: 0,
                                    fontWeight: 700,
                                    borderRadius: 999,
                                    borderColor: alpha(theme.palette.warning.main, 0.45),
                                    bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.08 : 0.12),
                                    color: theme.palette.warning.dark,
                                  })}
                                />
                              ) : null}
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', textAlign: 'center' }}
                            >
                              {studentProgress.currentIndex} / {studentProgress.total}{' '}
                              {t('tests.run.words', { defaultValue: 'слов' })}
                              {run.started_at ? (
                                <>
                                  {' · '}
                                  {t('tests.run.startedShort', { defaultValue: 'начат в' })}{' '}
                                  {formatClockTimeHHmm(run.started_at, i18n.language)}
                                </>
                              ) : null}
                            </Typography>
                            {canStudentCompleteRun ? (
                              <Button
                                size="small"
                                variant="text"
                                color="inherit"
                                disabled={completingRun}
                                onClick={() => void handleStudentCompleteRun()}
                                sx={{ textTransform: 'none', alignSelf: 'center', color: 'text.secondary' }}
                              >
                                {completingRun ? t('common.saving') : t('tests.run.completeNow')}
                              </Button>
                            ) : null}
                          </Stack>
                        </Box>
                      </Paper>

                      {/* Right column (sticky on large screens) */}
                      <Box
                        sx={{
                          order: { xs: 1, lg: 2 },
                          gridColumn: { lg: '2' },
                          gridRow: { lg: 2 },
                          position: { lg: 'sticky' },
                          top: { lg: 16 },
                          alignSelf: { lg: 'start' },
                        }}
                      >
                        <Stack spacing={2} alignItems="stretch">

                          {/* Active word card */}
                          <Paper
                            variant="outlined"
                            sx={(theme) => ({
                              borderRadius: 2.5,
                              bgcolor: 'background.paper',
                              borderLeft: '4px solid',
                              borderLeftColor: activeItem
                                ? studentItemIndicatorColor(activeItem, true, theme)
                                : alpha(theme.palette.text.primary, 0.2),
                            })}
                          >
                            <Box
                              sx={{
                                px: { xs: 2, sm: 2.5 },
                                // меньше padding по Y, но сохраняем "высоту карточки"
                                pt: { xs: 2.5, sm: 3 },
                                pb: { xs: 2.5, sm: 3 },
                                minHeight: { xs: 210, sm: 220 },
                              }}
                            >
                              {!activeItem ? (
                                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                                  {studentTeacherWaitingCopy}
                                </Typography>
                              ) : (
                                <Stack spacing={2} alignItems="stretch" sx={{ minHeight: 0 }}>
                                  <Box
                                    sx={{
                                      textAlign: 'center',
                                      position: 'relative',
                                      // vertical centering of the word block
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      flex: 1,
                                      minHeight: { xs: 150, sm: 160 },
                                      py: 0.5,
                                    }}
                                  >
                                    {activeItem.prompt_type === PROMPT_SOURCE_TEXT ? (
                                      <Typography
                                        component="div"
                                        sx={{
                                          fontSize: { xs: 34, sm: 42 },
                                          fontWeight: 800,
                                          lineHeight: 1.2,
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {(activeItem.card?.word?.text && activeItem.card.word.text.trim()) ||
                                          t('tests.run.fallbackCard', { id: activeItem.card_id })}
                                      </Typography>
                                    ) : activeItem.prompt_type === PROMPT_TRANSLATION_TEXT ? (
                                      <Typography
                                        component="div"
                                        sx={{
                                          fontSize: { xs: 34, sm: 42 },
                                          fontWeight: 800,
                                          lineHeight: 1.2,
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {cardTranslationLine(activeItem.card ?? undefined)}
                                      </Typography>
                                    ) : activeItem.prompt_type === PROMPT_AUDIO ? (
                                      <Stack spacing={1} alignItems="center">
                                        <IconButton
                                          onClick={() => void handleStudentAudioPlayed()}
                                          sx={(theme) => ({
                                            width: 56,
                                            height: 56,
                                            borderRadius: '50%',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              theme.palette.mode === 'light' ? 0.10 : 0.18,
                                            ),
                                            border: '1px solid',
                                            borderColor: alpha(
                                              theme.palette.primary.main,
                                              theme.palette.mode === 'light' ? 0.35 : 0.45,
                                            ),
                                          })}
                                          aria-label={t('tests.student.playAudio', { defaultValue: 'Воспроизвести' })}
                                        >
                                          <VolumeUpRoundedIcon sx={{ color: 'primary.main' }} />
                                        </IconButton>
                                        <Typography variant="body2" color="text.secondary">
                                          {t('tests.student.tapToListen', { defaultValue: 'нажмите, чтобы прослушать' })}
                                        </Typography>
                                      </Stack>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" textAlign="center">
                                        {t('tests.item.promptType')}: {activeItem.prompt_type}
                                      </Typography>
                                    )}

                                    {studentTranscriptionOpen && itemAllowsStudentTranscriptionByGoals(activeItem) ? (
                                      <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{
                                          mt: 0.75,
                                          fontFamily: 'monospace',
                                          wordBreak: 'break-word',
                                          fontSize: { xs: '1.05rem', sm: '1.15rem' },
                                          lineHeight: 1.35,
                                        }}
                                      >
                                        {(() => {
                                          const tr = getWordAttr(activeItem.card?.word, 'transcription')
                                          return tr ? `[${tr}]` : '—'
                                        })()}
                                      </Typography>
                                    ) : null}

                                    {/* chips in top-right corner of active card */}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        display: 'flex',
                                        gap: 0.75,
                                        flexWrap: 'wrap',
                                        justifyContent: 'flex-end',
                                        maxWidth: '60%',
                                      }}
                                    >
                                      {itemEvaluatedSkillList(activeItem).map((sk, idx) => {
                                        const ans = findAnswerForEvaluatedSkill(activeItem, sk)
                                        return (
                                          <Chip
                                            key={`${sk}-${idx}`}
                                            size="small"
                                            label={skillChipShortLabel(sk, t)}
                                            variant="outlined"
                                            sx={(theme) => ({
                                              fontSize: '0.7rem',
                                              ...chipSxForTeacherEvalSkill(activeItem, sk, ans, theme),
                                            })}
                                          />
                                        )
                                      })}
                                    </Box>
                                  </Box>

                                </Stack>
                              )}
                            </Box>
                          </Paper>

                          {/* Answer block */}
                          <Paper
                            variant="outlined"
                            sx={(theme) => ({
                              borderRadius: 2.5,
                              bgcolor: 'background.paper',
                              borderLeft: '4px solid',
                              borderLeftColor: activeItem
                                ? studentItemIndicatorColor(activeItem, true, theme)
                                : alpha(theme.palette.text.primary, 0.2),
                            })}
                          >
                            <Box
                              sx={{
                                px: { xs: 2, sm: 2.5 },
                                pt: { xs: 2.5, sm: 3 },
                                pb: { xs: 2.5, sm: 3 },
                              }}
                            >
                              <Stack spacing={1.5}>
                            {autoCheckFeedback?.itemId === activeItem?.id &&
                            autoCheckFeedback?.verdict === 'correct' ? (
                              <Alert severity="success" variant="outlined">
                                {t('tests.student.autoCheckCorrect')}
                              </Alert>
                            ) : null}
                            {autoCheckFeedback?.itemId === activeItem?.id &&
                            autoCheckFeedback?.verdict === 'incorrect' ? (
                              <Alert severity="error" variant="outlined">
                                {t('tests.student.autoCheckIncorrect')}
                              </Alert>
                            ) : null}

                            {!activeItem ? (
                              <Typography variant="body2" color="text.secondary">
                                {studentTeacherWaitingCopy}
                              </Typography>
                            ) : studentOralLessonCard ? (
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  bgcolor: (theme) => alpha(theme.palette.info.main, theme.palette.mode === 'light' ? 0.06 : 0.12),
                                  borderColor: (theme) => alpha(theme.palette.info.main, theme.palette.mode === 'light' ? 0.18 : 0.28),
                                }}
                              >
                                <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                  {t('tests.student.oralLesson')}
                                </Typography>
                                {studentAnswered ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                    {t('tests.run.awaitingReview')}
                                  </Typography>
                                ) : null}
                              </Paper>
                            ) : (
                              (() => {
                                const at = (activeItem.answer_type ?? '').trim()
                                const mode = answerTypeFormatMode(at)
                                const isChoose = mode === 'choose'
                                const isTyped = mode === 'typed' || mode === null
                                const isSpeech = mode === 'speech'
                                const disabled = submitting || studentAnswered

                                const choices = isChoose ? extractChoiceOptions(activeItem) : []
                                const placeholder =
                                  activeItem.prompt_type === 'source_text' && isTyped
                                    ? t('tests.student.placeholderSource', { defaultValue: 'Введите слово на английском...' })
                                    : t('tests.student.placeholderTranslation', { defaultValue: 'Введите перевод...' })

                                return (
                                  <Stack spacing={1.25}>
                                    {isSpeech ? (
                                      <Paper
                                        variant="outlined"
                                        sx={{
                                          p: 1.5,
                                          borderRadius: 2,
                                          bgcolor: (theme) => alpha(theme.palette.action.hover, 0.04),
                                        }}
                                      >
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                          {t('tests.student.speechOnlyTeacher')}
                                        </Typography>
                                      </Paper>
                                    ) : null}

                                    {isTyped ? (
                                      <TextField
                                        value={answerText}
                                        onChange={(e) => setAnswerText(e.target.value)}
                                        fullWidth
                                        placeholder={placeholder}
                                        disabled={disabled}
                                        inputProps={{ readOnly: disabled }}
                                      />
                                    ) : null}

                                    {isChoose ? (
                                      choices.length > 0 ? (
                                        <Box
                                          sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                            gap: 1,
                                          }}
                                        >
                                          {choices.map((c) => {
                                            const selected = studentSelectedChoice === c
                                            const locked = disabled && !selected
                                            return (
                                              <Button
                                                key={c}
                                                variant={selected ? 'contained' : 'outlined'}
                                                color={selected ? 'primary' : 'inherit'}
                                                onClick={() => {
                                                  if (disabled) return
                                                  setStudentSelectedChoice(c)
                                                }}
                                                sx={(theme) => ({
                                                  textTransform: 'none',
                                                  justifyContent: 'flex-start',
                                                  ...(locked
                                                    ? { opacity: 0.55, borderColor: alpha(theme.palette.text.primary, 0.12) }
                                                    : null),
                                                })}
                                                disabled={disabled}
                                              >
                                                {c}
                                              </Button>
                                            )
                                          })}
                                        </Box>
                                      ) : (
                                        <Alert severity="info" variant="outlined">
                                          {t('tests.student.noChoicesYet', { defaultValue: 'Варианты ответа не загружены для этой карточки.' })}
                                        </Alert>
                                      )
                                    ) : null}

                                    {!studentAnswered ? (
                                      <Button
                                        onClick={() => void handleSubmitAnswer()}
                                        variant="contained"
                                        fullWidth
                                        sx={{ textTransform: 'none', py: 1.1 }}
                                        disabled={
                                          !activeItem ||
                                          submitting ||
                                          (isChoose ? !(studentSelectedChoice ?? '').trim() : !(answerText ?? '').trim()) ||
                                          isSpeech
                                        }
                                      >
                                        {submitting ? t('common.saving') : t('tests.student.submit', { defaultValue: 'Ответить →' })}
                                      </Button>
                                    ) : (
                                      <Button variant="outlined" fullWidth disabled sx={{ textTransform: 'none', py: 1.1 }}>
                                        {t('tests.run.awaitingReview', { defaultValue: 'Ожидание учителя...' })}
                                      </Button>
                                    )}
                                  </Stack>
                                )
                              })()
                            )}

                            {/* hint / transcription links — только если цель «Перевод» / «Произношение» в активных целях карточки */}
                            {activeItem ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                                {itemAllowsStudentTranscriptionByGoals(activeItem) ? (
                                  !studentTranscriptionOpen ? (
                                    <Button
                                      variant="text"
                                      size="small"
                                      onClick={() => void handleStudentShowTranscription()}
                                      sx={{ textTransform: 'none', px: 0.5, minWidth: 0, fontSize: '0.8rem' }}
                                    >
                                      {t('tests.student.showTranscription', { defaultValue: 'Показать транскрипцию' })}
                                    </Button>
                                  ) : null
                                ) : null}
                                {itemAllowsStudentHintsByGoals(activeItem) ? (
                                  !studentCluesOpen ? (
                                    <Button
                                      variant="text"
                                      size="small"
                                      onClick={() => handleStudentShowClues()}
                                      sx={{ textTransform: 'none', px: 0.5, minWidth: 0, fontSize: '0.8rem' }}
                                    >
                                      {t('cards.clues', { defaultValue: 'Показать подсказки' })}
                                    </Button>
                                  ) : null
                                ) : null}
                              </Box>
                            ) : null}

                            {activeItem && activeItem.card && itemAllowsStudentHintsByGoals(activeItem) ? (
                              <Collapse in={studentCluesOpen} unmountOnExit>
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    mt: 1,
                                    bgcolor: (theme) => alpha(theme.palette.action.hover, 0.04),
                                  }}
                                >
                                  <CardClues
                                    key={activeItem.card.id}
                                    cardId={activeItem.card.id}
                                    wordId={activeItem.card.word_id}
                                    clues={activeItem.card.clues ?? []}
                                    protectedClueId={protectedClueId(activeItem.card)}
                                    protectedClueNote={t('cards.messages.clue_used_as_translation')}
                                    onClueAdded={(clue) => handleCardClueAdded(activeItem.card!.id, clue)}
                                    onClueDeleted={(clueId) =>
                                      handleCardClueDeleted(activeItem.card!.id, clueId)
                                    }
                                  />
                                </Paper>
                              </Collapse>
                            ) : null}
                          </Stack>
                        </Box>
                      </Paper>
                    </Stack>
                  </Box>
                </Box>
                  ) : null}
                </Box>
              </>
            ) : null}
          </>
        )}
        </Box>
      </Box>
    </DashboardLayout>
  )
}

